import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import config from '../config/config';
import { useAuth } from '../context/AuthContext';
import './VigilRuleStudio.css';

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Constants                                                                 */
/* ═══════════════════════════════════════════════════════════════════════════ */

const SEV = {
  critical: { color: '#dc2626', bg: 'rgba(220,38,38,.10)', icon: '🔴' },
  high:     { color: '#ea580c', bg: 'rgba(234,88,12,.10)',  icon: '🟠' },
  medium:   { color: '#ca8a04', bg: 'rgba(202,138,4,.10)',  icon: '🟡' },
  low:      { color: '#2563eb', bg: 'rgba(37,99,235,.10)',  icon: '🔵' },
  info:     { color: '#6b7280', bg: 'rgba(107,114,128,.10)',icon: '⚪' },
};

const LANGUAGES = ['go', 'java', 'python', 'javascript', 'typescript', 'c', 'cpp'];
const LANG_LABELS = {
  go: 'Go', java: 'Java', python: 'Python',
  javascript: 'JavaScript', typescript: 'TypeScript', c: 'C', cpp: 'C++',
};

const LANG_CLASS = {
  go: 'go', java: 'java', python: 'python', javascript: 'javascript',
  typescript: 'typescript', c: 'c', 'c++': 'cpp', cpp: 'cpp',
  dockerfile: 'dockerfile', yaml: 'yaml', hcl: 'hcl',
};

const FW_CLASS = {
  'EU-CRA': 'eucra', 'PCI-DSS': 'pcidss', 'OWASP': 'owasp',
  'ASVS': 'asvs', 'SOC2': 'soc2',
};
const FW_COLOR = {
  'EU-CRA': '#7c3aed', 'PCI-DSS': '#dc2626', 'OWASP': '#ea580c',
  'ASVS': '#0891b2', 'SOC2': '#059669',
};
const ALL_LANGS = ['Go','Java','Python','JavaScript','TypeScript','C','C++','Dockerfile','YAML','HCL'];
const ALL_FRAMEWORKS = ['EU-CRA','PCI-DSS','OWASP','ASVS','SOC2'];

const OWASP_TOP10 = [
  { id: 'A01', name: 'Broken Access Control' },
  { id: 'A02', name: 'Cryptographic Failures' },
  { id: 'A03', name: 'Injection' },
  { id: 'A04', name: 'Insecure Design' },
  { id: 'A05', name: 'Security Misconfiguration' },
  { id: 'A06', name: 'Vulnerable Components' },
  { id: 'A07', name: 'Authentication Failures' },
  { id: 'A08', name: 'Data Integrity Failures' },
  { id: 'A09', name: 'Logging & Monitoring Failures' },
  { id: 'A10', name: 'SSRF' },
];

const PIPELINE_STEPS = [
  { key: 'generating', label: 'Generate', icon: '🤖', desc: 'AI writes the rule' },
  { key: 'validating', label: 'Validate', icon: '✓',  desc: 'Syntax & semantics' },
  { key: 'testing',    label: 'Test',     icon: '🧪', desc: 'Fixture verification' },
  { key: 'done',       label: 'Score',    icon: '📊', desc: 'Quality assessment' },
];

const QUERY_EXAMPLES = [
  'Find SQL injection in Go',
  'Detect hardcoded secrets',
  'Find XSS vulnerabilities in React',
  'Check for weak cryptography',
  'Find path traversal attacks',
  'Detect insecure deserialization in Java',
];

const QUERY_HISTORY_KEY = 'vrs_query_history';
const MAX_QUERY_HISTORY = 10;

const VALID_SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];
const VALID_CONFIDENCES = ['high', 'medium', 'low'];

/* ── Templates ────────────────────────────────────────────────────────────── */
const TEMPLATES = {
  empty: `# Vigil Rule — Custom Rule\n\nrule my-custom-rule:\n  version: "1.0.0"\n  severity: medium\n  confidence: medium\n  languages: [go]\n  cwe: ["CWE-000"]\n\n  query: |\n    find flow\n      from @user_input\n      to @sql.execute\n\n  report:\n    message: "Describe the security issue here."\n    fix: "Describe the remediation here."\n`,
  sqli: `# Vigil Rule — SQL Injection Detection\n\nrule sql-injection:\n  version: "1.0.0"\n  severity: high\n  confidence: medium\n  languages: [go, java, python, javascript]\n  cwe: ["CWE-89"]\n  owasp: ["A03:2021-Injection"]\n\n  query: |\n    find flow\n      from @user_input\n      to @sql.execute\n      where not uses(@parameterized_query)\n      within @http_handler\n\n  exclude:\n    paths: ["**/tests/**", "**/vendor/**"]\n\n  report:\n    message: "User input flows to SQL execution without parameterization."\n    fix: "Use parameterized queries or an ORM."\n`,
  xss: `# Vigil Rule — Cross-Site Scripting\n\nrule xss-detection:\n  version: "1.0.0"\n  severity: high\n  confidence: medium\n  languages: [javascript, typescript]\n  cwe: ["CWE-79"]\n  owasp: ["A03:2021-Injection"]\n\n  query: |\n    find flow\n      from @user_input\n      to @html.render\n      where not uses(@html.escape)\n\n  report:\n    message: "User input rendered to HTML without escaping — XSS risk."\n    fix: "Sanitize output with html.escape or a safe rendering library."\n`,
  'path-traversal': `# Vigil Rule — Path Traversal\n\nrule path-traversal:\n  version: "1.0.0"\n  severity: high\n  confidence: medium\n  languages: [go, java, python]\n  cwe: ["CWE-22"]\n  owasp: ["A01:2021-Broken Access Control"]\n\n  query: |\n    find flow\n      from @user_input\n      to @file.write\n      where not uses(@path.clean)\n\n  report:\n    message: "User-controlled path used in file operation without sanitization."\n    fix: "Use filepath.Clean() and validate paths against allowed directories."\n`,
  'hardcoded-secret': `# Vigil Rule — Hardcoded Secrets\n\nrule hardcoded-secrets:\n  version: "1.0.0"\n  severity: critical\n  confidence: high\n  languages: [go, java, python, javascript]\n  cwe: ["CWE-798"]\n  owasp: ["A02:2021-Cryptographic Failures"]\n\n  query: |\n    find usage\n      of @credentials.hardcoded\n\n  exclude:\n    paths: ["**/tests/**", "**/*_test.go"]\n\n  report:\n    message: "Hardcoded credential detected in source code."\n    fix: "Use environment variables or a secrets manager."\n`,
  'weak-crypto': `# Vigil Rule — Weak Cryptographic Algorithm\n\nrule weak-crypto:\n  version: "1.0.0"\n  severity: high\n  confidence: high\n  languages: [go, java, python, javascript]\n  cwe: ["CWE-327"]\n  owasp: ["A02:2021-Cryptographic Failures"]\n\n  query: |\n    find call\n      to any(@crypto.md5, @crypto.sha1)\n\n  report:\n    message: "Weak cryptographic algorithm detected (MD5/SHA-1)."\n    fix: "Use SHA-256 or stronger. For passwords, use bcrypt or argon2."\n`,
  xxe: `# Vigil Rule — XML External Entity\n\nrule xxe-injection:\n  version: "1.0.0"\n  severity: critical\n  confidence: medium\n  languages: [java, python]\n  cwe: ["CWE-611"]\n  owasp: ["A05:2021-Security Misconfiguration"]\n\n  query: |\n    find flow\n      from @user_input\n      to @xml.parse\n      where not uses(@xml.disable_dtd)\n\n  report:\n    message: "XML parsed from user input without disabling external entities."\n    fix: "Disable DOCTYPE declarations and external entity resolution."\n`,
  ssrf: `# Vigil Rule — Server-Side Request Forgery\n\nrule ssrf-detection:\n  version: "1.0.0"\n  severity: high\n  confidence: medium\n  languages: [go, java, python, javascript]\n  cwe: ["CWE-918"]\n  owasp: ["A10:2021-Server-Side Request Forgery"]\n\n  query: |\n    find flow\n      from @user_input\n      to @http.outbound_request\n\n  report:\n    message: "User-controlled URL in outbound HTTP request — SSRF risk."\n    fix: "Validate and whitelist allowed URLs or domains."\n`,
  'insecure-deser': `# Vigil Rule — Insecure Deserialization\n\nrule insecure-deserialization:\n  version: "1.0.0"\n  severity: critical\n  confidence: medium\n  languages: [java, python]\n  cwe: ["CWE-502"]\n  owasp: ["A08:2021-Software and Data Integrity Failures"]\n\n  query: |\n    find flow\n      from @user_input\n      to @deserialize\n\n  report:\n    message: "User input flows to deserialization without validation."\n    fix: "Use type-safe serialization or validate data before deserialization."\n`,
  'open-redirect': `# Vigil Rule — Open Redirect\n\nrule open-redirect:\n  version: "1.0.0"\n  severity: medium\n  confidence: medium\n  languages: [go, java, python, javascript]\n  cwe: ["CWE-601"]\n  owasp: ["A01:2021-Broken Access Control"]\n\n  query: |\n    find flow\n      from @user_input\n      to @url.redirect\n\n  report:\n    message: "User-controlled URL in redirect — open redirect risk."\n    fix: "Validate redirect destinations against an allowlist."\n`,
};

/* ── Concept browser ──────────────────────────────────────────────────────── */
const CONCEPTS = {
  source:    ['@user_input', '@env_var', '@file_read', '@credentials.hardcoded'],
  sink:      ['@sql.execute', '@os.exec', '@http.outbound_request', '@file.write', '@logging.write', '@html.render', '@url.redirect', '@deserialize', '@template.render'],
  sanitizer: ['@parameterized_query', '@html.escape', '@url.encode', '@path.clean', '@input.sanitize'],
  guard:     ['@http_handler', '@auth.middleware', '@rate_limit'],
  crypto:    ['@crypto.md5', '@crypto.sha1', '@crypto.random', '@crypto.weak_random', '@jwt.signing'],
  import:    ['@import.crypto', '@import.unsafe'],
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  DSL Utilities                                                             */
/* ═══════════════════════════════════════════════════════════════════════════ */

const DSL_KEYWORDS = new Set([
  'rule', 'version', 'severity', 'confidence', 'languages', 'cwe', 'owasp',
  'query', 'find', 'flow', 'call', 'usage', 'ref', 'from', 'to', 'where',
  'within', 'not', 'uses', 'any', 'all', 'and', 'or', 'passes_through',
  'calls', 'matches', 'in', 'language', 'file', 'exclude', 'report',
  'message', 'fix', 'evidence', 'tests', 'fixture', 'expect', 'engines',
  'literal', 'function', 'of', 'paths', 'pattern', 'import', 'class',
  'annotation', 'variable', 'assignment', 'extends', 'implements',
]);

function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/* Returns an HTML string (for dangerouslySetInnerHTML in the code editor) */
function highlightDSLHtml(text) {
  return text
    .split('\n')
    .map(line => {
      if (line.trimStart().startsWith('#')) {
        return `<span class="cmt">${escapeHtml(line)}</span>`;
      }
      return line.replace(
        /(@[\w.]+)|("(?:[^"\\]|\\.)*")|(\b\d+\b)|(\b[a-z_]+\b)/g,
        (match, concept, str, num, word) => {
          if (concept) return `<span class="concept">${escapeHtml(concept)}</span>`;
          if (str)     return `<span class="str">${escapeHtml(str)}</span>`;
          if (num)     return `<span class="num">${escapeHtml(num)}</span>`;
          if (word && DSL_KEYWORDS.has(word)) return `<span class="kw">${escapeHtml(word)}</span>`;
          return escapeHtml(match);
        }
      );
    })
    .join('\n');
}

/* Returns React elements (for <pre> displays) */
function highlightDSL(code) {
  if (!code) return null;
  const combined = new RegExp(
    '(//[^\n]*)'                          +  // comments
    '|("(?:[^"\\\\]|\\\\.)*"'            +  // double-quoted strings
    "|'(?:[^'\\\\]|\\\\.)*')"            +  // single-quoted strings
    '|(@[a-zA-Z_][\\w.]*)'               +  // @concepts
    '|(\\b(?:from|to|where|as)\\b)'      +  // flow keywords
    '|(\\b(?:find|flow|match|taint|source|sink|sanitizer|not|and|or|in|rule|query|severity|confidence|languages|cwe|owasp|report|message|fix|version|exclude|paths|within|uses|any|all|calls|passes_through)\\b)',
    'gm'
  );
  const parts = [];
  let last = 0, m;
  while ((m = combined.exec(code)) !== null) {
    if (m.index > last) parts.push(<span key={last}>{code.slice(last, m.index)}</span>);
    if      (m[1]) parts.push(<span key={m.index} className="vrs-dsl-comment">{m[0]}</span>);
    else if (m[2]) parts.push(<span key={m.index} className="vrs-dsl-string">{m[0]}</span>);
    else if (m[3]) parts.push(<span key={m.index} className="vrs-dsl-concept">{m[0]}</span>);
    else if (m[4]) parts.push(<span key={m.index} className="vrs-dsl-flow">{m[0]}</span>);
    else if (m[5]) parts.push(<span key={m.index} className="vrs-dsl-keyword">{m[0]}</span>);
    else           parts.push(<span key={m.index}>{m[0]}</span>);
    last = m.index + m[0].length;
  }
  if (last < code.length) parts.push(<span key={last}>{code.slice(last)}</span>);
  return parts;
}

/* Parse key metadata out of raw rule DSL */
function parseRule(text) {
  const result = {
    id: '', severity: '', confidence: '', version: '',
    languages: [], cwe: [], owasp: [],
    query: '', message: '', fix: '', excludePaths: [],
  };
  const lines = text.split('\n');
  const idMatch = text.match(/^rule\s+([\w-]+)\s*:/m);
  if (idMatch) result.id = idMatch[1];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const kv = line.match(/^(version|severity|confidence):\s*"?([^"\n]+)"?\s*$/);
    if (kv) { result[kv[1]] = kv[2].trim(); continue; }
    const lang = line.match(/^languages:\s*\[([^\]]*)\]/);
    if (lang) { result.languages = lang[1].split(',').map(s => s.trim().replace(/"/g, '')).filter(Boolean); continue; }
    const cwe = line.match(/^cwe:\s*\[([^\]]*)\]/);
    if (cwe) { result.cwe = cwe[1].split(',').map(s => s.trim().replace(/"/g, '')).filter(Boolean); continue; }
    const owasp = line.match(/^owasp:\s*\[([^\]]*)\]/);
    if (owasp) { result.owasp = owasp[1].split(',').map(s => s.trim().replace(/"/g, '')).filter(Boolean); continue; }
    if (line.match(/^query:\s*\|/)) {
      const qLines = [];
      for (let j = i + 1; j < lines.length; j++) {
        if (lines[j].match(/^\S/) && !lines[j].match(/^\s*$/)) break;
        if (lines[j].trim() === '' && j > i + 1) {
          const nxt = lines.slice(j + 1).find(l => l.trim() !== '');
          if (nxt && !nxt.startsWith('    ') && !nxt.startsWith('\t')) break;
        }
        qLines.push(lines[j]);
      }
      result.query = qLines.map(l => l.replace(/^ {4}/, '')).join('\n').trim();
      continue;
    }
    const paths = line.match(/^paths:\s*\[([^\]]*)\]/);
    if (paths) { result.excludePaths = paths[1].split(',').map(s => s.trim().replace(/"/g, '')).filter(Boolean); continue; }
    const msg = line.match(/^message:\s*"([^"]*)"/);
    if (msg) { result.message = msg[1]; continue; }
    const fix = line.match(/^fix:\s*"([^"]*)"/);
    if (fix) { result.fix = fix[1]; continue; }
  }
  return result;
}

function validateRuleDSL(parsed) {
  const errors = [];
  if (!parsed.id)       errors.push('Missing rule ID (rule <name>:)');
  if (!parsed.severity) errors.push('Missing severity');
  else if (!VALID_SEVERITIES.includes(parsed.severity)) errors.push(`Invalid severity: ${parsed.severity}`);
  if (parsed.confidence && !VALID_CONFIDENCES.includes(parsed.confidence)) errors.push(`Invalid confidence: ${parsed.confidence}`);
  if (parsed.languages.length === 0) errors.push('Missing languages');
  if (!parsed.query)    errors.push('Missing query block');
  if (!parsed.message)  errors.push('Missing report.message');
  return errors;
}

/* Helpers */
function statusIndex(s) {
  const map = { pending: -1, generating: 0, validating: 1, testing: 2, calibrating: 3, done: 3, approved: 3, failed: -2 };
  return map[s] ?? -1;
}
function statusBadgeStyle(status) {
  const colors = {
    pending: '#6b7280', generating: '#8b5cf6', validating: '#3b82f6',
    testing: '#f59e0b', calibrating: '#06b6d4', done: '#10b981',
    failed: '#ef4444', approved: '#059669',
  };
  return { backgroundColor: colors[status] || '#6b7280' };
}
function scoreColor(score) {
  if (score >= 70) return '#10b981';
  if (score >= 40) return '#f59e0b';
  return '#ef4444';
}
function parseJSON(str) {
  if (!str) return null;
  if (typeof str === 'object') return str;
  try { return JSON.parse(str); } catch { return null; }
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return d; }
}
function shortPath(p) {
  if (!p) return '';
  const parts = p.split('/');
  return parts.length > 3 ? '…/' + parts.slice(-3).join('/') : p;
}
function severityColor(sev) {
  switch ((sev || '').toLowerCase()) {
    case 'critical': return '#dc2626';
    case 'high':     return '#ef4444';
    case 'medium':   return '#f59e0b';
    case 'low':      return '#22c55e';
    default:         return '#64748b';
  }
}
function loadQueryHistory() {
  try { return JSON.parse(localStorage.getItem(QUERY_HISTORY_KEY) || '[]'); }
  catch { return []; }
}
function saveQueryHistory(list) {
  localStorage.setItem(QUERY_HISTORY_KEY, JSON.stringify(list.slice(0, MAX_QUERY_HISTORY)));
}
function wrapDSLAsRule(dsl, description) {
  return `# Vigil Rule — ${description || 'From Query'}\n\nrule my-query-rule:\n  version: "1.0.0"\n  severity: medium\n  confidence: medium\n  languages: [go, javascript, python]\n\n  query: |\n    ${dsl.replace(/\n/g, '\n    ')}\n\n  report:\n    message: "Issue detected."\n    fix: "Review and fix the flagged code."\n`;
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Shared UI Atoms                                                           */
/* ═══════════════════════════════════════════════════════════════════════════ */

function Spinner({ size = 'sm' }) {
  return <span className={`vrs-spinner${size === 'lg' ? ' vrs-spinner-lg' : ''}`} />;
}

function AnimatedCounter({ value, duration = 600 }) {
  const ref = useRef(null);
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current, end = value;
    if (start === end) return;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      if (ref.current) ref.current.textContent = Math.round(start + (end - start) * ease);
      if (p < 1) requestAnimationFrame(tick);
      else prev.current = end;
    };
    requestAnimationFrame(tick);
  }, [value, duration]);
  return <span ref={ref}>{prev.current}</span>;
}

function QualityGauge({ score }) {
  const r = 54, stroke = 10, circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score || 0));
  const offset = circ - (pct / 100) * circ;
  const color = scoreColor(pct);
  return (
    <div className="vrs-gauge">
      <svg viewBox="0 0 140 140" className="vrs-gauge__svg">
        <circle cx="70" cy="70" r={r} fill="none" stroke="var(--bs-border-color,#333)" strokeWidth={stroke} opacity="0.25" />
        <circle cx="70" cy="70" r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          transform="rotate(-90 70 70)" className="vrs-gauge__arc" />
        <text x="70" y="64" textAnchor="middle" fill={color} fontSize="28" fontWeight="700">{pct}</text>
        <text x="70" y="84" textAnchor="middle" fill="var(--bs-secondary-color,#999)" fontSize="11" fontWeight="500">Quality</text>
      </svg>
    </div>
  );
}

function PipelineViz({ status }) {
  const idx = statusIndex(status);
  const failed = status === 'failed';
  return (
    <div className="vrs-pipeline">
      {PIPELINE_STEPS.map((step, i) => {
        const isActive = !failed && idx === i;
        const isDone   = !failed && idx > i;
        const isFail   = failed && i === 0;
        let cls = 'vrs-pipeline__step';
        if (isDone)   cls += ' vrs-pipeline__step--done';
        if (isActive) cls += ' vrs-pipeline__step--active';
        if (isFail)   cls += ' vrs-pipeline__step--failed';
        return (
          <React.Fragment key={step.key}>
            {i > 0 && (
              <div className={`vrs-pipeline__connector${isDone || isActive ? ' vrs-pipeline__connector--lit' : ''}`} />
            )}
            <div className={cls}>
              <div className="vrs-pipeline__icon">
                {isDone ? '✓' : isFail ? '✕' : step.icon}
              </div>
              <span className="vrs-pipeline__label">{step.label}</span>
              <span className="vrs-pipeline__desc">{step.desc}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function CalibrationModal({ job, token, onClose, onDone }) {
  const [repoPath, setRepoPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');
  const run = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.post(`${config.BASE_URL}/vigil/rulesmith/calibrate`,
        { jobId: job.id, repoPath },
        { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
      if (onDone) onDone();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    setLoading(false);
  };
  const cal = result ? parseJSON(result.calibrationRes || result) : null;
  return (
    <div className="vrs-modal-backdrop" onClick={onClose}>
      <div className="vrs-modal" onClick={e => e.stopPropagation()}>
        <button className="vrs-modal-close" onClick={onClose}>✕</button>
        <h3>⚗️ Calibrate Rule</h3>
        <p style={{ fontSize: '0.87rem', color: 'var(--bs-secondary-color)', marginBottom: '1rem' }}>
          Run <strong>{job.ruleId || job.id}</strong> against a real repository to measure FP rate.
        </p>
        <label className="vrs-label">Repository Path</label>
        <input className="vrs-input" placeholder="/path/to/repo or https://github.com/..."
          value={repoPath} onChange={e => setRepoPath(e.target.value)} />
        {error && <div className="vrs-error">{error}</div>}
        <div className="vrs-modal-actions">
          <button className="vrs-btn" onClick={onClose}>Cancel</button>
          <button className="vrs-btn vrs-btn--primary" onClick={run} disabled={loading || !repoPath}>
            {loading ? <><Spinner /> Calibrating…</> : 'Run Calibration'}
          </button>
        </div>
        {cal && (
          <div className="vrs-cal-results">
            <div className="vrs-cal-stat">
              <span className="vrs-cal-stat__num">{cal.totalFindings ?? '—'}</span>
              <span className="vrs-cal-stat__label">Total</span>
            </div>
            <div className="vrs-cal-stat vrs-cal-stat--tp">
              <span className="vrs-cal-stat__num">{cal.tpCount ?? '—'}</span>
              <span className="vrs-cal-stat__label">True Positives</span>
            </div>
            <div className="vrs-cal-stat vrs-cal-stat--fp">
              <span className="vrs-cal-stat__num">{cal.fpCount ?? '—'}</span>
              <span className="vrs-cal-stat__label">False Positives</span>
            </div>
            <div style={{ width: '100%', textAlign: 'center', fontSize: '0.85rem', color: 'var(--bs-secondary-color)' }}>
              FP Rate: <strong>{cal.fpRate || '0%'}</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Tab 1: Browse                                                             */
/* ═══════════════════════════════════════════════════════════════════════════ */

function BrowseTab({ token, openTab, similarRules }) {
  const { t } = useTranslation();
  const [packs, setPacks]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [search, setSearch]             = useState('');
  const [sevFilter, setSevFilter]       = useState(null);
  const [langFilter, setLangFilter]     = useState('');
  const [fwFilter, setFwFilter]         = useState(null);
  const [tagFilter, setTagFilter]       = useState(null);
  const [expandedPack, setExpandedPack] = useState(null);
  const [expandedRules, setExpandedRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [drawerRule, setDrawerRule]     = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [showCoverage, setShowCoverage] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchPacks = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(`${config.BASE_URL}/vigil/rulepacks`, { headers });
      setPacks(data.packs || []);
    } catch (err) { setError(err.response?.data?.error || err.message); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchPacks(); }, [fetchPacks]);

  const handleExpandPack = useCallback(async (packId) => {
    if (expandedPack === packId) { setExpandedPack(null); return; }
    setExpandedPack(packId); setLoadingRules(true);
    try {
      const { data } = await axios.get(`${config.BASE_URL}/vigil/rulepacks/${packId}`, { headers });
      setExpandedRules(data.rules || []);
    } catch { setExpandedRules([]); }
    setLoadingRules(false);
  }, [expandedPack, headers]);

  const openRuleDrawer = useCallback(async (ruleId) => {
    setDrawerLoading(true); setDrawerRule(null);
    try {
      const { data } = await axios.get(`${config.BASE_URL}/vigil/rules/${encodeURIComponent(ruleId)}`, { headers });
      setDrawerRule(data);
    } catch { setDrawerRule(null); }
    setDrawerLoading(false);
  }, [headers]);

  const stats = useMemo(() => {
    const totalRules = packs.reduce((s, p) => s + (p.ruleCount || 0), 0);
    const allLangs = new Set(), allFw = new Set();
    packs.forEach(p => {
      (p.languages || []).forEach(l => allLangs.add(l));
      (p.frameworks || []).forEach(f => allFw.add(f));
    });
    return { totalRules, totalPacks: packs.length, languages: allLangs.size, frameworks: allFw.size };
  }, [packs]);

  const allTags = useMemo(() => {
    const s = new Set();
    packs.forEach(p => (p.tags || []).forEach(tag => s.add(tag)));
    return [...s].sort();
  }, [packs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return packs.filter(p => {
      if (q && ![p.name, p.description, ...(p.tags || [])].join(' ').toLowerCase().includes(q)) return false;
      if (sevFilter && !(p.severities || {})[sevFilter]) return false;
      if (langFilter && !(p.languages || []).some(l => l.toLowerCase() === langFilter.toLowerCase())) return false;
      if (fwFilter && !(p.frameworks || []).includes(fwFilter)) return false;
      if (tagFilter && !(p.tags || []).includes(tagFilter)) return false;
      return true;
    });
  }, [packs, search, sevFilter, langFilter, fwFilter, tagFilter]);

  /* OWASP coverage: which categories have rules */
  const owaspCoverage = useMemo(() => {
    const counts = {};
    packs.forEach(p => {
      (p.owasp || []).forEach(o => {
        const match = o.match(/A(\d+)/);
        if (match) {
          const key = `A${match[1].padStart(2, '0')}`;
          counts[key] = (counts[key] || 0) + 1;
        }
      });
    });
    return counts;
  }, [packs]);

  const renderHeatmap = (sev) => {
    const total = Object.values(sev || {}).reduce((s, v) => s + v, 0);
    if (!total) return <div className="vrs-heatmap" />;
    return (
      <div className="vrs-heatmap">
        {['critical','high','medium','low','info'].map(k => {
          const pct = ((sev[k] || 0) / total) * 100;
          return pct > 0 ? <div key={k} className={`vrs-heatmap-seg vrs-heatmap-${k}`} style={{ width: `${pct}%` }} /> : null;
        })}
      </div>
    );
  };

  if (loading) return <div className="vrs-loading"><div className="vrs-spinner-lg" /><span>{t('common.loading','Loading…')}</span></div>;
  if (error)   return <div className="vrs-empty"><div className="vrs-empty__icon">⚠️</div><p>{error}</p><button className="vrs-btn vrs-btn--primary" onClick={fetchPacks}>{t('common.retry','Retry')}</button></div>;

  return (
    <div className="vrs-browse">
      {/* Stats row */}
      <motion.div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1rem' }}
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        {[
          { icon: '📋', value: stats.totalRules,  label: 'Total Rules' },
          { icon: '📦', value: stats.totalPacks,  label: 'Rule Packs' },
          { icon: '💻', value: stats.languages,   label: 'Languages' },
          { icon: '🏛️', value: stats.frameworks,  label: 'Frameworks' },
        ].map((s, i) => (
          <div className="vrs-card" key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.4rem', marginBottom: '0.2rem' }}>{s.icon}</div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--accent, #6366f1)' }}>
              <AnimatedCounter value={s.value} />
            </div>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--bs-secondary-color)' }}>{s.label}</div>
          </div>
        ))}
      </motion.div>

      {/* Similar rules panel (from Generate tab search) */}
      {similarRules && similarRules.length > 0 && (
        <div className="vrs-similar-panel">
          <h4>🔍 {t('vigilStudio.similarRules','Similar existing rules')}</h4>
          {similarRules.map(r => (
            <div key={r.id} className="vrs-similar-item" onClick={() => openRuleDrawer(r.id)}>
              <span className="vrs-similar-id">{r.id}</span>
              {r.pack && <span className="vrs-lang-tag">{r.pack}</span>}
              {r.severity && <span className={`vrs-sev-dot ${r.severity}`}>{r.severity}</span>}
              {r.score != null && <span className="vrs-similar-score">{Math.round(r.score * 100)}%</span>}
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="vrs-filters">
        <div className="vrs-filter-row">
          <input className="vrs-input vrs-search" placeholder={t('vigilRules.searchPlaceholder','Search packs…')}
            value={search} onChange={e => setSearch(e.target.value)} />
          <select className="vrs-input vrs-select vrs-lang-select" value={langFilter} onChange={e => setLangFilter(e.target.value)}>
            <option value="">All Languages</option>
            {ALL_LANGS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <button className="vrs-btn vrs-btn--sm" onClick={() => setShowCoverage(v => !v)}>
            {showCoverage ? '✕' : '🗺️'} {t('vigilStudio.coverageMap','Coverage Map')}
          </button>
          {(search || sevFilter || langFilter || fwFilter || tagFilter) && (
            <button className="vrs-filter-clear" onClick={() => { setSearch(''); setSevFilter(null); setLangFilter(''); setFwFilter(null); setTagFilter(null); }}>
              Clear filters
            </button>
          )}
        </div>
        <div className="vrs-filter-row">
          <div className="vrs-chips">
            <button className={`vrs-chip${!sevFilter ? ' active' : ''}`} onClick={() => setSevFilter(null)}>All</button>
            {Object.entries(SEV).map(([k, v]) => (
              <button key={k} className={`vrs-chip${sevFilter === k ? ' active' : ''}`}
                style={{ '--chip-color': v.color }} onClick={() => setSevFilter(sevFilter === k ? null : k)}>
                <span className="vrs-chip-dot" style={{ background: v.color }} />{k}
              </button>
            ))}
          </div>
        </div>
        <div className="vrs-filter-row">
          <div className="vrs-chips">
            {ALL_FRAMEWORKS.map(fw => (
              <button key={fw} className={`vrs-fw-chip${fwFilter === fw ? ' active' : ''}`}
                style={{ '--fw-color': FW_COLOR[fw] }} onClick={() => setFwFilter(fwFilter === fw ? null : fw)}>
                {fw}
              </button>
            ))}
          </div>
        </div>
        {allTags.length > 0 && (
          <div className="vrs-filter-row">
            <div className="vrs-tag-cloud">
              {allTags.map(tag => (
                <button key={tag} className={`vrs-tag${tagFilter === tag ? ' active' : ''}`}
                  onClick={() => setTagFilter(tagFilter === tag ? null : tag)}>{tag}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* OWASP Coverage Map */}
      <AnimatePresence>
        {showCoverage && (
          <motion.div className="vrs-coverage-map"
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}>
            <h4>🗺️ OWASP Top 10 Coverage</h4>
            <div className="vrs-coverage-grid">
              {OWASP_TOP10.map(o => {
                const count = owaspCoverage[o.id] || 0;
                const covered = count > 0;
                return (
                  <div key={o.id} className={`vrs-coverage-cell ${covered ? 'vrs-coverage-cell--covered' : 'vrs-coverage-cell--uncovered'}`}>
                    <span className="vrs-coverage-cell__id">{o.id}</span>
                    <span className="vrs-coverage-cell__name">{o.name}</span>
                    {covered && <span className="vrs-coverage-cell__count">{count} rule{count !== 1 ? 's' : ''}</span>}
                    {!covered && <span className="vrs-coverage-cell__count" style={{ color: '#ef4444' }}>None</span>}
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pack grid */}
      {filtered.length === 0 ? (
        <div className="vrs-empty"><div className="vrs-empty__icon">📭</div><p>No rule packs found.</p></div>
      ) : (
        <div className="vrs-pack-grid">
          <AnimatePresence>
            {filtered.map(pack => (
              <React.Fragment key={pack.id}>
                <motion.div
                  className={`vrs-pack-card${expandedPack === pack.id ? ' expanded' : ''}`}
                  onClick={() => handleExpandPack(pack.id)}
                  layout initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.18 }}>
                  <div className="vrs-pack-count">{pack.ruleCount} rules</div>
                  <div className="vrs-pack-header">
                    <h3 className="vrs-pack-name">{pack.name}</h3>
                    <span className="vrs-pack-version">v{pack.version}</span>
                  </div>
                  <p className="vrs-pack-desc">{pack.description}</p>
                  {renderHeatmap(pack.severities)}
                  <div className="vrs-lang-badges">
                    {(pack.languages || []).map(l => (
                      <span key={l} className={`vrs-lang-badge vrs-lang-${LANG_CLASS[l.toLowerCase()] || 'c'}`}>{l}</span>
                    ))}
                  </div>
                  {(pack.tags || []).length > 0 && (
                    <div className="vrs-pack-tags">
                      {pack.tags.map(tag => <span key={tag} className="vrs-pack-tag">{tag}</span>)}
                    </div>
                  )}
                  {(pack.frameworks || []).length > 0 && (
                    <div className="vrs-fw-badges">
                      {pack.frameworks.map(fw => (
                        <span key={fw} className={`vrs-fw-badge vrs-fw-${FW_CLASS[fw] || 'owasp'}`}>{fw}</span>
                      ))}
                    </div>
                  )}
                </motion.div>

                {expandedPack === pack.id && (
                  <motion.div className="vrs-expanded-panel"
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.22 }}>
                    {loadingRules ? (
                      <div className="vrs-loading" style={{ padding: '2rem' }}><div className="vrs-spinner-lg" /></div>
                    ) : (
                      <table className="vrs-rules-table">
                        <thead>
                          <tr>
                            <th>Severity</th><th>Rule ID</th><th>Confidence</th>
                            <th>Languages</th><th>CWE</th><th>OWASP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expandedRules.map(rule => (
                            <tr key={rule.id} onClick={() => openRuleDrawer(rule.id)}>
                              <td><span className={`vrs-sev-dot ${rule.severity}`}>{rule.severity}</span></td>
                              <td><span className="vrs-rule-id">{rule.id}</span></td>
                              <td><span className={`vrs-conf-badge vrs-conf-${rule.confidence || 'medium'}`}>{rule.confidence || 'medium'}</span></td>
                              <td>
                                <div className="vrs-lang-badges">
                                  {(rule.languages || []).map(l => (
                                    <span key={l} className={`vrs-lang-badge vrs-lang-${LANG_CLASS[l.toLowerCase()] || 'c'}`}>{l}</span>
                                  ))}
                                </div>
                              </td>
                              <td>
                                <div className="vrs-drawer-badges-row">
                                  {(rule.cwe || []).map(c => (
                                    <a key={c} href={`https://cwe.mitre.org/data/definitions/${c}.html`}
                                      target="_blank" rel="noopener noreferrer" className="vrs-cwe-badge"
                                      onClick={e => e.stopPropagation()}>CWE-{c}</a>
                                  ))}
                                </div>
                              </td>
                              <td>
                                <div className="vrs-drawer-badges-row">
                                  {(rule.owasp || []).map(o => <span key={o} className="vrs-owasp-badge">{o}</span>)}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </motion.div>
                )}
              </React.Fragment>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Rule Drawer */}
      <AnimatePresence>
        {(drawerRule || drawerLoading) && (
          <>
            <motion.div className="vrs-drawer-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => { setDrawerRule(null); setDrawerLoading(false); }} />
            <motion.div className="vrs-drawer"
              initial={{ x: 480 }} animate={{ x: 0 }} exit={{ x: 480 }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}>
              {drawerLoading ? (
                <div className="vrs-loading" style={{ flex: 1 }}><div className="vrs-spinner-lg" /></div>
              ) : drawerRule && (
                <>
                  <div className="vrs-drawer-header">
                    <div>
                      <div className="vrs-drawer-title">{drawerRule.id}</div>
                      <div className="vrs-drawer-badges">
                        <span className={`vrs-sev-dot ${drawerRule.severity}`}>{drawerRule.severity}</span>
                        <span className={`vrs-conf-badge vrs-conf-${drawerRule.confidence || 'medium'}`}>
                          {drawerRule.confidence || 'medium'}
                        </span>
                      </div>
                    </div>
                    <button className="vrs-drawer-close" onClick={() => setDrawerRule(null)}>✕</button>
                  </div>
                  <div className="vrs-drawer-body">
                    <div className="vrs-drawer-section">
                      <h4>Classification</h4>
                      <div className="vrs-drawer-badges-row" style={{ marginBottom: '0.5rem' }}>
                        {(drawerRule.cwe || []).map(c => (
                          <a key={c} href={`https://cwe.mitre.org/data/definitions/${c.replace(/\D/g,'')}.html`}
                            target="_blank" rel="noopener noreferrer" className="vrs-cwe-badge">CWE-{c}</a>
                        ))}
                      </div>
                      <div className="vrs-drawer-badges-row">
                        {(drawerRule.owasp || []).map(o => <span key={o} className="vrs-owasp-badge">{o}</span>)}
                      </div>
                    </div>
                    <div className="vrs-drawer-section">
                      <h4>Languages</h4>
                      <div className="vrs-lang-badges">
                        {(drawerRule.languages || []).map(l => (
                          <span key={l} className={`vrs-lang-badge vrs-lang-${LANG_CLASS[l.toLowerCase()] || 'c'}`}>{l}</span>
                        ))}
                      </div>
                    </div>
                    {drawerRule.query && (
                      <div className="vrs-drawer-section">
                        <h4>Detection Query</h4>
                        <pre className="vrs-dsl-block">{highlightDSL(drawerRule.query)}</pre>
                      </div>
                    )}
                    {drawerRule.message && (
                      <div className="vrs-drawer-section">
                        <h4>Finding Message</h4>
                        <div className="vrs-message-box">{drawerRule.message}</div>
                      </div>
                    )}
                    {drawerRule.fix && (
                      <div className="vrs-drawer-section">
                        <h4>Remediation</h4>
                        <div className="vrs-fix-box">{drawerRule.fix}</div>
                      </div>
                    )}
                  </div>
                  <div className="vrs-drawer-actions">
                    <button className="vrs-btn vrs-btn--primary" onClick={() => {
                      if (drawerRule.rawContent) openTab('write', { ruleContent: drawerRule.rawContent });
                      setDrawerRule(null);
                    }}>
                      ✏️ {t('vigilStudio.forkToWrite','Fork to Write')}
                    </button>
                    <button className="vrs-btn" onClick={() => setDrawerRule(null)}>Close</button>
                  </div>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Tab 2: Write                                                              */
/* ═══════════════════════════════════════════════════════════════════════════ */

function WriteTab({ token, draftRule, setDraftRule }) {
  const { t } = useTranslation();
  const textareaRef  = useRef(null);
  const highlightRef = useRef(null);
  const linesRef     = useRef(null);

  const [validating, setValidating]     = useState(false);
  const [validResult, setValidResult]   = useState(null); // { valid, errors }
  const [localErrors, setLocalErrors]   = useState([]);
  const [copyOk, setCopyOk]             = useState(false);
  const [conceptOpen, setConceptOpen]   = useState(false);
  const [playgroundOpen, setPlaygroundOpen] = useState(false);
  const [snippet, setSnippet]           = useState('');
  const [snippetLang, setSnippetLang]   = useState('auto');
  const [testLoading, setTestLoading]   = useState(false);
  const [testResults, setTestResults]   = useState(null);
  const [testError, setTestError]       = useState('');
  const [showRawOutput, setShowRawOutput] = useState(false);
  const [saveOpen, setSaveOpen]         = useState(false);
  const [loadOpen, setLoadOpen]         = useState(false);
  const [saveName, setSaveName]         = useState('');
  const [saveDesc, setSaveDesc]         = useState('');
  const [saving, setSaving]             = useState(false);
  const [savedRules, setSavedRules]     = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [saveMsg, setSaveMsg]           = useState('');

  const parsed    = useMemo(() => parseRule(draftRule), [draftRule]);
  const lineCount = useMemo(() => draftRule.split('\n').length, [draftRule]);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Detect pattern/language mismatch and suggest the correct function keyword
  const patternHint = useMemo(() => {
    // In Auto mode we test all rule languages — mismatch hint doesn't apply
    if (snippetLang === 'auto') return null;
    if (!testResults || (testResults.findings?.length ?? 0) > 0) return null;
    const query = (parsed.query || draftRule).toLowerCase();
    const lang  = snippetLang.toLowerCase();
    const tips = {
      go:         { keyword: 'func',     example: 'func $NAME(...) { }' },
      java:       { keyword: 'void',     example: 'void $NAME($PARAMS) { }' },
      python:     { keyword: 'def',      example: 'def $NAME($PARAMS): ...' },
      javascript: { keyword: 'function', example: 'function $NAME($PARAMS) { }' },
      typescript: { keyword: 'function', example: 'function $NAME($PARAMS): $RETURN { }' },
      ruby:       { keyword: 'def',      example: 'def $NAME($PARAMS); end' },
      csharp:     { keyword: 'void',     example: 'void $NAME($PARAMS) { }' },
    };
    const correct = tips[lang];
    if (!correct) return null;
    const wrongKeywords = Object.entries(tips)
      .filter(([l]) => l !== lang)
      .map(([, v]) => v.keyword);
    const hasWrongKeyword = wrongKeywords.some(kw => new RegExp(`\\b(find|pattern|\\").*\\b${kw}\\b`).test(query));
    const missingCorrect = !query.includes(correct.keyword);
    if (hasWrongKeyword || missingCorrect) {
      return { lang, example: correct.example };
    }
    return null;
  }, [testResults, parsed.query, draftRule, snippetLang]);

  /* scroll sync */
  const handleScroll = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    if (highlightRef.current) { highlightRef.current.scrollTop = ta.scrollTop; highlightRef.current.scrollLeft = ta.scrollLeft; }
    if (linesRef.current) linesRef.current.scrollTop = ta.scrollTop;
  }, []);

  /* tab key */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = e.target;
      const start = ta.selectionStart, end = ta.selectionEnd;
      const newCode = draftRule.substring(0, start) + '  ' + draftRule.substring(end);
      setDraftRule(newCode);
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = start + 2; });
    }
  }, [draftRule, setDraftRule]);

  /* insert concept */
  const insertConcept = useCallback((concept) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const newCode = draftRule.substring(0, start) + concept + draftRule.substring(end);
    setDraftRule(newCode);
    requestAnimationFrame(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + concept.length; });
  }, [draftRule, setDraftRule]);

  /* validate */
  const handleValidate = useCallback(async () => {
    const local = validateRuleDSL(parsed);
    setLocalErrors(local);
    if (local.length > 0) { setValidResult(null); return; }
    setValidating(true); setValidResult(null);
    try {
      const { data } = await axios.post(`${config.BASE_URL}/vigil/rules/validate`, { rule: draftRule }, { headers });
      setValidResult(data);
    } catch (e) {
      setValidResult({ valid: false, errors: [e.response?.data?.error || e.message] });
    }
    setValidating(false);
  }, [parsed, draftRule, headers]);

  /* test with snippet */
  const handleTest = useCallback(async () => {
    setTestLoading(true); setTestError(''); setTestResults(null);
    try {
      const { data } = await axios.post(`${config.BASE_URL}/vigil/rules/test`,
        { rule: draftRule, snippet: snippet || undefined, language: snippetLang },
        { headers });
      setTestResults(data);
    } catch (e) { setTestError(e.response?.data?.error || e.message); }
    setTestLoading(false);
  }, [draftRule, snippet, snippetLang, headers]);

  /* copy */
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(draftRule).then(() => { setCopyOk(true); setTimeout(() => setCopyOk(false), 2000); });
  }, [draftRule]);

  /* download */
  const handleDownload = useCallback(() => {
    const filename = parsed.id ? `${parsed.id}.rule` : 'rule.rule';
    const blob = new Blob([draftRule], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }, [draftRule, parsed.id]);

  /* save */
  const handleSave = useCallback(async () => {
    if (!saveName.trim()) return;
    setSaving(true); setSaveMsg('');
    try {
      await axios.post(`${config.BASE_URL}/vigil/rules/user`, {
        name: saveName.trim(),
        description: saveDesc.trim(),
        content: draftRule,
        severity: parsed.severity || 'medium',
        languages: parsed.languages.join(','),
        status: 'draft',
        source: 'studio',
      }, { headers });
      setSaveMsg('Saved!');
      setTimeout(() => { setSaveOpen(false); setSaveMsg(''); setSaveName(''); setSaveDesc(''); }, 1200);
    } catch (e) { setSaveMsg(e.response?.data?.error || e.message); }
    setSaving(false);
  }, [saveName, saveDesc, draftRule, parsed, headers]);

  /* load saved rules */
  const handleOpenLoad = useCallback(async () => {
    setLoadOpen(true); setLoadingRules(true);
    try {
      const { data } = await axios.get(`${config.BASE_URL}/vigil/rules/user`, { headers });
      setSavedRules(data.rules || []);
    } catch { setSavedRules([]); }
    setLoadingRules(false);
  }, [headers]);

  const loadRule = useCallback((rule) => {
    setDraftRule(rule.content || '');
    setLoadOpen(false);
    setValidResult(null);
    setLocalErrors([]);
  }, [setDraftRule]);

  const deleteRule = useCallback(async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${config.BASE_URL}/vigil/rules/user/${id}`, { headers });
      setSavedRules(prev => prev.filter(r => r.id !== id));
    } catch { /* ignore */ }
  }, [headers]);

  const sevClass = parsed.severity ? parsed.severity : '';
  const validated = validResult !== null || localErrors.length > 0;
  const hasError  = localErrors.length > 0 || (validResult && !validResult.valid);

  return (
    <div className="vrs-write">
      {/* top bar */}
      <div className="vrs-write-topbar">
        {parsed.id && <span className="vrs-rule-name">{parsed.id}</span>}
        {parsed.severity && (
          <span className={`vrs-sev-dot ${sevClass}`} style={{ marginLeft: parsed.id ? 0 : 0 }}>{parsed.severity}</span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          <button className="vrs-btn vrs-btn--sm" onClick={handleValidate} disabled={validating}>
            {validating ? <><Spinner /> …</> : t('vigilStudio.validate','Validate')}
          </button>
          <button className="vrs-btn vrs-btn--sm" onClick={handleTest} disabled={testLoading}>
            {testLoading ? <><Spinner /> …</> : t('vigilStudio.test','Test')}
          </button>
          <button className="vrs-btn vrs-btn--sm" onClick={() => setSaveOpen(true)}>{t('vigilStudio.save','Save')}</button>
          <button className="vrs-btn vrs-btn--sm" onClick={handleOpenLoad}>{t('vigilStudio.load','Load')}</button>
          <button className="vrs-btn vrs-btn--sm" onClick={handleDownload}>↓ Download</button>
          <button className="vrs-btn vrs-btn--sm" onClick={handleCopy}>{copyOk ? '✓ Copied' : '📋 Copy'}</button>
          <button className="vrs-btn vrs-btn--sm" onClick={() => setPlaygroundOpen(v => !v)}>
            {playgroundOpen ? '✕' : '🎮'} {t('vigilStudio.playground','Playground')}
          </button>
        </div>
      </div>

      <div className="vrs-write-split">
        {/* LEFT: editor */}
        <div className="vrs-write-left">
          {/* template selector */}
          <div className="vrs-template-row">
            <label className="vrs-label" style={{ marginTop: 0, marginBottom: 0 }}>Template:</label>
            <select className="vrs-input vrs-select" style={{ maxWidth: 260 }}
              defaultValue="" onChange={e => { if (e.target.value) { setDraftRule(TEMPLATES[e.target.value]); setValidResult(null); setLocalErrors([]); } }}>
              <option value="" disabled>— select template —</option>
              <option value="empty">Empty (blank)</option>
              <option value="sqli">SQL Injection</option>
              <option value="xss">XSS</option>
              <option value="path-traversal">Path Traversal</option>
              <option value="hardcoded-secret">Hardcoded Secret</option>
              <option value="weak-crypto">Weak Crypto</option>
              <option value="xxe">XXE Injection</option>
              <option value="ssrf">SSRF</option>
              <option value="insecure-deser">Insecure Deserialization</option>
              <option value="open-redirect">Open Redirect</option>
            </select>
          </div>

          {/* code editor */}
          <div className={`vrs-editor-wrapper${hasError && validated ? ' has-error' : ''}`} style={{ flex: 1 }}>
            <div className="vrs-editor-container">
              <div className="vrs-editor-lines" ref={linesRef}>
                {Array.from({ length: lineCount }, (_, i) => <div key={i}>{i + 1}</div>)}
              </div>
              <pre className="vrs-editor-highlight" ref={highlightRef} aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: highlightDSLHtml(draftRule) + '\n' }} />
              <textarea className="vrs-editor-textarea" ref={textareaRef}
                value={draftRule}
                onChange={e => { setDraftRule(e.target.value); setValidResult(null); setLocalErrors([]); }}
                onScroll={handleScroll}
                onKeyDown={handleKeyDown}
                spellCheck={false} autoComplete="off" autoCorrect="off" autoCapitalize="off" />
            </div>
          </div>

          {/* playground */}
          <AnimatePresence>
            {playgroundOpen && (
              <motion.div className="vrs-playground"
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }}>
                <h4>🎮 {t('vigilStudio.playground','Rule Playground')}
                  <span style={{ fontWeight: 400, fontSize: '0.78rem', color: 'var(--bs-secondary-color)', marginLeft: '0.5rem' }}>
                    Paste a code snippet — no need for <code>package</code>/<code>class</code> boilerplate
                  </span>
                </h4>
                <div className="vrs-playground-row">
                  <select className="vrs-input vrs-select" style={{ maxWidth: 160 }}
                    value={snippetLang} onChange={e => setSnippetLang(e.target.value)}>
                    <option value="auto">🤖 Auto (all languages)</option>
                    {LANGUAGES.map(l => <option key={l} value={l}>{LANG_LABELS[l]}</option>)}
                  </select>
                  <button className="vrs-btn vrs-btn--primary" onClick={handleTest} disabled={testLoading}>
                    {testLoading ? <><Spinner /> Testing…</> : '▶ Test Rule'}
                  </button>
                  <span style={{ fontSize: '0.76rem', color: 'var(--bs-secondary-color)', marginLeft: 'auto' }}>
                    {snippetLang === 'auto'
                      ? `Tests against: ${(parsed.languages?.length ? parsed.languages : ['all declared languages']).join(', ')}`
                      : ({go:'func $N()',python:'def $N():',javascript:'function $N(){}',typescript:'function $N(){}',java:'void $N(){}',ruby:'def $N; end'}[snippetLang] || 'language-specific pattern')}
                  </span>
                </div>
                <textarea className="vrs-input vrs-textarea" rows={6}
                  placeholder="Paste code snippet here…"
                  value={snippet} onChange={e => setSnippet(e.target.value)} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* RIGHT: preview + validation + concepts */}
        <div className="vrs-write-right">
          {/* preview card */}
          <div className="vrs-preview-card">
            <div className="vrs-preview-header">Rule Preview</div>
            <div className="vrs-preview-body">
              {[
                ['Rule ID', parsed.id || '—'],
                ['Severity', parsed.severity ? <span className={`vrs-sev-dot ${parsed.severity}`}>{parsed.severity}</span> : '—'],
                ['Confidence', parsed.confidence || '—'],
                ['Languages', parsed.languages.length > 0 ? parsed.languages.map(l => <span key={l} className="vrs-lang-tag" style={{ marginRight: 2 }}>{l}</span>) : '—'],
                ['CWE', parsed.cwe.length > 0 ? parsed.cwe.map(c => <a key={c} href={`https://cwe.mitre.org/data/definitions/${c.replace(/\D/g,'')}.html`} target="_blank" rel="noopener noreferrer" className="vrs-cwe-badge" style={{ marginRight: 2 }}>{c}</a>) : '—'],
                ['OWASP', parsed.owasp.length > 0 ? parsed.owasp.map(o => <span key={o} className="vrs-owasp-badge" style={{ marginRight: 2 }}>{o}</span>) : '—'],
                ['Message', parsed.message || '—'],
                ['Fix', parsed.fix || '—'],
              ].map(([label, value]) => (
                <div className="vrs-preview-row" key={label}>
                  <span className="vrs-preview-label">{label}</span>
                  <span className="vrs-preview-value">{value}</span>
                </div>
              ))}
              {parsed.query && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div className="vrs-preview-label" style={{ marginBottom: '0.35rem' }}>Query</div>
                  <pre className="vrs-dsl-block" style={{ fontSize: '0.78rem', padding: '0.6rem' }}>
                    {highlightDSL(parsed.query)}
                  </pre>
                </div>
              )}
            </div>
          </div>

          {/* validation results */}
          {(localErrors.length > 0 || validResult) && (
            <div className={`vrs-validation ${hasError ? 'vrs-validation--fail' : 'vrs-validation--ok'}`}>
              <div className="vrs-validation-header">
                {hasError ? '❌ Validation Failed' : '✅ Validation Passed'}
              </div>
              {(localErrors.length > 0 || (validResult?.errors?.length > 0)) && (
                <ul className="vrs-validation-errors">
                  {[...(localErrors), ...(validResult?.errors || [])].map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* test results */}
          {(testError || testResults) && (
            <div className="vrs-test-results">
              <h4>Test Results</h4>
              {testError && <div className="vrs-error">{testError}</div>}
              {testResults && (
                <>
                  <div style={{ fontSize: '0.84rem', marginBottom: '0.5rem', color: 'var(--bs-secondary-color)' }}>
                    {testResults.findings?.length ?? 0} finding(s)
                    {testResults.duration && ` · ${testResults.duration}`}
                    {snippetLang === 'auto' && testResults.findings?.length > 0 && (
                      <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>
                        (tested as all rule languages)
                      </span>
                    )}
                  </div>
                  {testResults.findings?.length > 0 ? (
                    <div className="vrs-findings-list">
                      {testResults.findings.map((f, i) => {
                        const langMatch = (f.file || '').match(/playground_([a-z]+)\.[a-z]+$/i);
                        const matchedLang = langMatch ? langMatch[1] : (f.language || snippetLang);
                        const line = f.startLine ?? f.line;
                        return (
                          <div key={i} className="vrs-finding-card">
                            <div className="vrs-finding-card-header">
                              <span className="vrs-sev-dot" style={{ background: `${severityColor(f.severity)}20`, color: severityColor(f.severity) }}>{f.severity}</span>
                              <span className="vrs-lang-tag" style={{ marginLeft: '0.4rem' }}>{matchedLang}</span>
                              {line && <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--bs-secondary-color)', marginLeft: '0.4rem' }}>line {line}</span>}
                              <span style={{ fontSize: '0.76rem', color: 'var(--bs-secondary-color)', marginLeft: 'auto' }}>{f.ruleId || f.ruleID}</span>
                            </div>
                            {f.snippet && (
                              <pre className="vrs-finding-snippet">{f.snippet}</pre>
                            )}
                            <div className="vrs-finding-msg">{f.message}</div>
                            {f.captures && Object.keys(f.captures).length > 0 && (
                              <div className="vrs-finding-captures">
                                {Object.entries(f.captures).map(([k,v]) => (
                                  <span key={k} className="vrs-hint-chip"><em>{k}</em> → <code>{v}</code></span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div>
                      <div className="vrs-no-findings">✓ No findings in snippet</div>
                      {snippetLang === 'auto' && parsed.languages?.length > 0 && (
                        <div className="vrs-test-hint">
                          <strong>💡 Tested against: {parsed.languages.join(', ')}</strong> — no pattern matched in any language.
                          Check that your snippet syntax matches what the pattern expects for at least one language.
                          <span className="vrs-hint-chips">
                            {[['go','func $N() { }'],['python','def $N(): pass'],['javascript','function $N() { }']].map(([l,e]) => (
                              <code key={l} className="vrs-hint-chip"><em>{l}:</em> {e}</code>
                            ))}
                          </span>
                        </div>
                      )}
                      {/* Single-language mismatch hint */}
                      {patternHint && (
                        <div className="vrs-test-hint">
                          <strong>💡 Pattern tip for {patternHint.lang}:</strong> patterns are language-specific.
                          For <code>{patternHint.lang}</code>, try: <code>{patternHint.example}</code>
                          <span style={{ display: 'block', marginTop: '0.4rem' }}>
                            Or switch the language selector to <strong>🤖 Auto</strong> to test against all rule languages at once.
                          </span>
                        </div>
                      )}
                      {!patternHint && snippetLang !== 'auto' && (
                        <div className="vrs-test-hint">
                          Possible reasons: pattern doesn't match, rule excluded the file path, or the snippet has a syntax error.
                          Language-specific function patterns:
                          <span className="vrs-hint-chips">
                            {[['go','func $N() { }'],['python','def $N(): pass'],['javascript','function $N() { }'],['java','void $N() { }'],['typescript','function $N(): void { }']].map(([l,e]) => (
                              <code key={l} className="vrs-hint-chip"><em>{l}:</em> {e}</code>
                            ))}
                          </span>
                        </div>
                      )}
                      {/* Raw output toggle */}
                      {testResults.raw && testResults.raw.trim() && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <button className="vrs-btn vrs-btn--sm" onClick={() => setShowRawOutput(v => !v)}>
                            {showRawOutput ? '▾ Hide raw output' : '▸ Show raw output'}
                          </button>
                          {showRawOutput && (
                            <pre className="vrs-raw-output">{testResults.raw}</pre>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* concept browser */}
          <div className="vrs-concepts">
            <div className="vrs-concepts-header" onClick={() => setConceptOpen(v => !v)}>
              @Concept Browser
              <span>{conceptOpen ? '▾' : '▸'}</span>
            </div>
            <AnimatePresence>
              {conceptOpen && (
                <motion.div className="vrs-concepts-body"
                  initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }}>
                  {Object.entries(CONCEPTS).map(([cat, items]) => (
                    <div className="vrs-concept-cat" key={cat}>
                      <div className="vrs-concept-cat-label">{cat}</div>
                      <div className="vrs-concept-tags">
                        {items.map(c => (
                          <span key={c} className="vrs-concept-tag" onClick={() => insertConcept(c)} title={`Insert ${c}`}>{c}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* SAVE modal */}
      <AnimatePresence>
        {saveOpen && (
          <motion.div className="vrs-modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSaveOpen(false)}>
            <motion.div className="vrs-modal" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.18 }}
              onClick={e => e.stopPropagation()}>
              <button className="vrs-modal-close" onClick={() => setSaveOpen(false)}>✕</button>
              <h3>💾 Save Rule</h3>
              <label className="vrs-label">Name *</label>
              <input className="vrs-input" placeholder="My rule name…" value={saveName} onChange={e => setSaveName(e.target.value)} />
              <label className="vrs-label">Description</label>
              <textarea className="vrs-input vrs-textarea" rows={2} placeholder="Optional description…" value={saveDesc} onChange={e => setSaveDesc(e.target.value)} />
              {saveMsg && <div className={saveMsg === 'Saved!' ? 'vrs-success' : 'vrs-error'}>{saveMsg}</div>}
              <div className="vrs-modal-actions">
                <button className="vrs-btn" onClick={() => setSaveOpen(false)}>Cancel</button>
                <button className="vrs-btn vrs-btn--primary" onClick={handleSave} disabled={saving || !saveName.trim()}>
                  {saving ? <><Spinner /> Saving…</> : '💾 Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* LOAD modal */}
      <AnimatePresence>
        {loadOpen && (
          <motion.div className="vrs-modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLoadOpen(false)}>
            <motion.div className="vrs-modal" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.18 }}
              onClick={e => e.stopPropagation()}>
              <button className="vrs-modal-close" onClick={() => setLoadOpen(false)}>✕</button>
              <h3>📂 Load Rule</h3>
              {loadingRules ? (
                <div className="vrs-loading"><div className="vrs-spinner-lg" /></div>
              ) : savedRules.length === 0 ? (
                <div className="vrs-empty" style={{ padding: '2rem 0' }}>No saved rules yet.</div>
              ) : (
                savedRules.map(r => (
                  <div key={r.id} className="vrs-saved-rule-item" onClick={() => loadRule(r)}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="vrs-saved-rule-item__name">{r.name}</div>
                      <div className="vrs-saved-rule-item__meta">
                        {r.severity && <span className={`vrs-sev-dot ${r.severity}`} style={{ marginRight: 4 }}>{r.severity}</span>}
                        {r.languages && <span>{r.languages}</span>}
                        {r.description && <span style={{ marginLeft: 6 }}>{r.description}</span>}
                      </div>
                    </div>
                    <div className="vrs-saved-rule-item__actions">
                      <button className="vrs-btn vrs-btn--sm" onClick={e => deleteRule(r.id, e)} title="Delete">🗑️</button>
                    </div>
                  </div>
                ))
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Tab 3: Generate                                                           */
/* ═══════════════════════════════════════════════════════════════════════════ */

function GenerateTab({ token, openTab, onSimilarRulesChange }) {
  const { t } = useTranslation();
  const [description, setDescription] = useState('');
  const [languages, setLanguages]     = useState([]);
  const [severity, setSeverity]       = useState('high');
  const [generating, setGenerating]   = useState(false);
  const [genError, setGenError]       = useState('');
  const [activeJob, setActiveJob]     = useState(null);
  const [detailJob, setDetailJob]     = useState(null);
  const [calJob, setCalJob]           = useState(null);
  const pollRef = useRef(null);

  /* history */
  const [history, setHistory]       = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [sortCol, setSortCol]       = useState('createdAt');
  const [sortDir, setSortDir]       = useState('desc');

  /* variants */
  const [showVariants, setShowVariants]     = useState(false);
  const [variants, setVariants]             = useState([]);
  const [variantLoading, setVariantLoading] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  /* semantic pre-search */
  const [similarRules, setSimilarRules] = useState([]);
  const debounceRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const { data } = await axios.get(`${config.BASE_URL}/vigil/rulesmith/history?limit=50`, { headers });
      setHistory(data.jobs || []);
    } catch { /* ignore */ }
    setHistLoading(false);
  }, [headers]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  /* poll active job */
  useEffect(() => {
    if (!activeJob) return;
    const active = ['pending','generating','validating','testing','calibrating'];
    if (!active.includes(activeJob.status)) { clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${config.BASE_URL}/vigil/rulesmith/history?limit=50`, { headers });
        const updated = (data.jobs || []).find(j => j.id === activeJob.id);
        if (updated) {
          setActiveJob(updated);
          if (!active.includes(updated.status)) { clearInterval(pollRef.current); loadHistory(); }
        }
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [activeJob?.id, activeJob?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  /* debounced semantic search */
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (description.trim().length < 8) { setSimilarRules([]); onSimilarRulesChange([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${config.BASE_URL}/vigil/rules/similar`, { params: { q: description }, headers });
        const rules = data.rules || [];
        setSimilarRules(rules);
        onSimilarRulesChange(rules);
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [description, headers, onSimilarRulesChange]);

  const toggleLang = (lang) => setLanguages(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]);

  const handleGenerate = async () => {
    if (!description.trim() || languages.length === 0) return;
    setGenerating(true); setGenError(''); setDetailJob(null);
    try {
      const { data } = await axios.post(`${config.BASE_URL}/vigil/rulesmith/generate`,
        { description: description.trim(), languages, severity }, { headers });
      setActiveJob({ id: data.id, status: data.status || 'pending', description: description.trim() });
    } catch (e) { setGenError(e.response?.data?.error || e.message); }
    setGenerating(false);
  };

  const handleGenerateVariants = async () => {
    if (!description.trim() || languages.length === 0) return;
    setVariantLoading(true); setVariants([]);
    try {
      const { data } = await axios.post(`${config.BASE_URL}/vigil/rulesmith/variants`,
        { description: description.trim(), languages, severity, count: 3 }, { headers });
      setVariants(data.variants || []);
      setShowVariants(true);
    } catch (e) { setGenError(e.response?.data?.error || e.message); }
    setVariantLoading(false);
  };

  const handleApprove = async (job) => {
    const packId = window.prompt('Enter rule pack ID to install to (e.g. "custom")');
    if (!packId) return;
    try {
      await axios.post(`${config.BASE_URL}/vigil/rulesmith/approve`, { jobId: job.id, packId }, { headers });
      loadHistory();
      if (activeJob?.id === job.id) setActiveJob(prev => ({ ...prev, status: 'approved', approved: true, packId }));
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };
  const sorted = [...history].sort((a, b) => {
    let av = a[sortCol], bv = b[sortCol];
    if (sortCol === 'qualityScore') { av = av || 0; bv = bv || 0; }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const displayJob = detailJob || activeJob;
  const testResults = displayJob ? parseJSON(displayJob.testResults) : null;
  const calResults  = displayJob ? parseJSON(displayJob.calibrationRes) : null;

  return (
    <div className="vrs-generate">
      {/* Input form */}
      <div className="vrs-gen-input">
        <h3 className="vrs-section-title">⚡ Forge a Rule</h3>
        <label className="vrs-label">Description</label>
        <textarea className="vrs-input vrs-textarea" rows={3}
          placeholder="Describe what security pattern to detect… e.g. 'Find SQL injection via string concatenation in prepared statement arguments'"
          value={description} onChange={e => setDescription(e.target.value)} />

        <div className="vrs-gen-grid">
          <div>
            <label className="vrs-label">Languages</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {LANGUAGES.map(l => (
                <button key={l} type="button"
                  className={`vrs-chip${languages.includes(l) ? ' active' : ''}`}
                  style={{ '--chip-color': 'var(--accent, #6366f1)' }}
                  onClick={() => toggleLang(l)}>{LANG_LABELS[l]}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="vrs-label">Severity</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {Object.keys(SEV).map(s => (
                <button key={s} type="button"
                  className={`vrs-chip${severity === s ? ' active' : ''}`}
                  style={{ '--chip-color': SEV[s].color }}
                  onClick={() => setSeverity(s)}>
                  {SEV[s].icon} {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        {genError && <div className="vrs-error">{genError}</div>}

        <div className="vrs-gen-actions">
          <button className="vrs-btn vrs-btn--primary" disabled={generating || !description.trim() || languages.length === 0} onClick={handleGenerate}>
            {generating ? <><Spinner /> Generating…</> : '🔨 Generate Rule'}
          </button>
          <button className="vrs-btn" disabled={variantLoading || !description.trim() || languages.length === 0} onClick={handleGenerateVariants}>
            {variantLoading ? <><Spinner /> …</> : t('vigilStudio.generateVariants','Generate Variants')}
          </button>
        </div>
      </div>

      {/* Semantic pre-search */}
      {similarRules.length > 0 && (
        <div className="vrs-semantic-panel">
          <h4>🔍 {t('vigilStudio.similarRules','Similar existing rules')}
            <span style={{ fontWeight: 400, color: 'var(--bs-secondary-color)', marginLeft: '0.4rem' }}>
              — these might already cover your use case
            </span>
          </h4>
          {similarRules.slice(0, 5).map(r => (
            <div key={r.id} className="vrs-similar-item" onClick={() => openTab('browse')}>
              <span className="vrs-similar-id">{r.id}</span>
              {r.pack && <span className="vrs-lang-tag">{r.pack}</span>}
              {r.severity && <span className={`vrs-sev-dot ${r.severity}`}>{r.severity}</span>}
              {r.score != null && <span className="vrs-similar-score">{Math.round(r.score * 100)}% match</span>}
            </div>
          ))}
        </div>
      )}

      {/* Variants */}
      <AnimatePresence>
        {showVariants && variants.length > 0 && (
          <motion.div className="vrs-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 className="vrs-section-title" style={{ margin: 0 }}>🎲 {t('vigilStudio.variants','Rule Variants')}</h3>
              <button className="vrs-btn vrs-btn--sm" onClick={() => setShowVariants(false)}>✕ Close</button>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)', marginBottom: '1rem' }}>
              Select a variant to proceed with, or use one as a starting point in the Write tab.
            </p>
            <div className="vrs-variants-grid">
              {variants.map((v, i) => (
                <div key={v.id || i} className={`vrs-variant-card${selectedVariant === i ? ' vrs-variant-card--selected' : ''}`}
                  onClick={() => setSelectedVariant(i)}>
                  <h5>Variant {i + 1}</h5>
                  <pre className="vrs-dsl-block" style={{ fontSize: '0.76rem', maxHeight: 200, overflow: 'auto' }}>
                    {highlightDSL(v.content || v)}
                  </pre>
                  <button className="vrs-btn vrs-btn--sm vrs-btn--primary" style={{ marginTop: '0.5rem' }}
                    onClick={e => { e.stopPropagation(); openTab('write', { ruleContent: v.content || v }); }}>
                    ✏️ {t('vigilStudio.refineInWrite','Refine in Write')}
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pipeline */}
      {displayJob && (
        <div className="vrs-card">
          <h3 className="vrs-section-title">Pipeline — {displayJob.description?.substring(0, 60)}</h3>
          <PipelineViz status={displayJob.status} />
          {displayJob.status === 'failed' && displayJob.error && (
            <div className="vrs-error" style={{ marginTop: '0.75rem' }}>❌ {displayJob.error}</div>
          )}
        </div>
      )}

      {/* Results */}
      {displayJob && ['done','approved','calibrating'].includes(displayJob.status) && (
        <div className="vrs-card">
          <h3 className="vrs-section-title">📋 Results — {displayJob.ruleId || displayJob.id}</h3>
          <div className="vrs-results-grid">
            <div>
              <h4 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: '0.5rem' }}>Rule Content</h4>
              <pre className="vrs-dsl-block">{highlightDSL(displayJob.ruleContent || '(no content)')}</pre>
            </div>
            <div className="vrs-results-meta">
              <QualityGauge score={displayJob.qualityScore} />
              <div className="vrs-badge-row">
                <span className={displayJob.validationOk ? 'vrs-badge-ok' : 'vrs-badge-fail'}>
                  {displayJob.validationOk ? '✅ Valid' : '❌ Invalid'}
                </span>
                {displayJob.validationMsg && <span className="vrs-badge-msg">{displayJob.validationMsg}</span>}
              </div>
              {testResults && (
                <div className="vrs-card" style={{ padding: '0.75rem' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.4rem', color: 'var(--bs-secondary-color)' }}>Test Results</div>
                  <div style={{ fontSize: '0.82rem' }}>
                    <span className={testResults.vulnerableFindings > 0 ? 'vrs-badge-ok' : 'vrs-badge-fail'}>Vuln: {testResults.vulnerableFindings ?? 0}</span>
                    {' '}
                    <span className={testResults.safeFindings === 0 ? 'vrs-badge-ok' : 'vrs-badge-fail'}>Safe: {testResults.safeFindings ?? 0}</span>
                    {' '}
                    <span className={testResults.pass ? 'vrs-badge-ok' : 'vrs-badge-fail'}>{testResults.pass ? '✅ PASS' : '❌ FAIL'}</span>
                  </div>
                </div>
              )}
              {calResults && (
                <div className="vrs-card" style={{ padding: '0.75rem' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.4rem', color: 'var(--bs-secondary-color)' }}>Calibration</div>
                  <div style={{ fontSize: '0.82rem' }}>Total: {calResults.totalFindings} | FP Rate: <strong>{calResults.fpRate}</strong></div>
                </div>
              )}
              {displayJob.provider && (
                <span className="vrs-provider-badge">{displayJob.provider} / {displayJob.model}</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
            <button className="vrs-btn" onClick={() => setCalJob(displayJob)}>⚗️ Calibrate</button>
            <button className="vrs-btn vrs-btn--primary" onClick={() => handleApprove(displayJob)} disabled={displayJob.approved}>
              {displayJob.approved ? '✅ Approved' : '🚀 Approve & Install'}
            </button>
            {displayJob.ruleContent && (
              <button className="vrs-btn" onClick={() => openTab('write', { ruleContent: displayJob.ruleContent })}>
                ✏️ {t('vigilStudio.refineInWrite','Refine in Write')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* History table */}
      <div className="vrs-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 className="vrs-section-title" style={{ margin: 0 }}>📜 Generation History</h3>
          <button className="vrs-btn vrs-btn--sm" onClick={loadHistory} disabled={histLoading}>
            {histLoading ? <><Spinner /> …</> : '↻ Refresh'}
          </button>
        </div>
        {history.length === 0 && !histLoading ? (
          <div className="vrs-empty" style={{ padding: '2rem 0' }}>No rules generated yet.</div>
        ) : (
          <div className="vrs-history-table-wrap">
            <table className="vrs-history-table">
              <thead>
                <tr>
                  {[['description','Description'],['ruleId','Rule ID'],['languages','Languages'],['qualityScore','Quality'],['status','Status'],['createdAt','Created'],['_actions','']].map(([col, label]) => (
                    <th key={col} onClick={() => col !== '_actions' && handleSort(col)}>
                      {label}{sortCol === col && <span style={{ marginLeft: 2 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map(job => (
                  <React.Fragment key={job.id}>
                    <tr onClick={() => setExpandedId(expandedId === job.id ? null : job.id)}>
                      <td style={{ maxWidth: 220 }}>{job.description?.substring(0,80)}{job.description?.length > 80 ? '…' : ''}</td>
                      <td><code className="vrs-rule-id">{job.ruleId || '—'}</code></td>
                      <td>{(job.languages || '').split(',').filter(Boolean).map(l => <span key={l} className="vrs-lang-tag">{l}</span>)}</td>
                      <td>{job.qualityScore != null ? <span className="vrs-score-pill" style={{ color: scoreColor(job.qualityScore) }}>{job.qualityScore}</span> : '—'}</td>
                      <td><span className="vrs-status-badge" style={statusBadgeStyle(job.status)}>{job.status}</span></td>
                      <td style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{fmtDate(job.createdAt)}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <span style={{ display: 'flex', gap: '0.3rem' }}>
                          <button className="vrs-action-btn" title="View" onClick={() => { setDetailJob(job); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>👁</button>
                          <button className="vrs-action-btn" title="Calibrate" onClick={() => setCalJob(job)}>⚗️</button>
                          <button className="vrs-action-btn" title="Approve" onClick={() => handleApprove(job)} disabled={job.approved}>🚀</button>
                          {job.ruleContent && (
                            <button className="vrs-action-btn" title="Edit in Write" onClick={() => openTab('write', { ruleContent: job.ruleContent })}>✏️</button>
                          )}
                        </span>
                      </td>
                    </tr>
                    {expandedId === job.id && (
                      <tr className="vrs-history-table__detail-row">
                        <td colSpan={7}>
                          <div className="vrs-history-table__detail">
                            {job.ruleContent && (
                              <pre className="vrs-dsl-block" style={{ fontSize: '0.78rem', marginBottom: '0.5rem' }}>
                                {highlightDSL(job.ruleContent)}
                              </pre>
                            )}
                            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.81rem', color: 'var(--bs-secondary-color)' }}>
                              <span>Validation: {job.validationOk ? '✅' : '❌'} {job.validationMsg || ''}</span>
                              {job.provider && <span>Provider: {job.provider}/{job.model}</span>}
                              {job.error && <span style={{ color: '#ef4444' }}>Error: {job.error}</span>}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {calJob && (
        <CalibrationModal job={calJob} token={token}
          onClose={() => setCalJob(null)}
          onDone={() => { loadHistory(); setCalJob(null); }} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Tab 4: Query                                                              */
/* ═══════════════════════════════════════════════════════════════════════════ */

function QueryTab({ token, openTab }) {
  const { t } = useTranslation();
  const [mode, setMode]           = useState('ai');
  const [question, setQuestion]   = useState('');
  const [loading, setLoading]     = useState(false);
  const [aiResult, setAiResult]   = useState(null);
  const [aiError, setAiError]     = useState('');
  const [manualQuery, setManualQuery] = useState('');
  const [targetPath, setTargetPath]   = useState('');
  const [runLoading, setRunLoading]   = useState(false);
  const [runResult, setRunResult]     = useState(null);
  const [runError, setRunError]       = useState('');
  const [copied, setCopied]           = useState(false);
  const [history, setHistory]         = useState(loadQueryHistory);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => { saveQueryHistory(history); }, [history]);

  const translate = useCallback(async () => {
    const q = question.trim();
    if (!q) return;
    setLoading(true); setAiError(''); setAiResult(null);
    try {
      const { data } = await axios.post(`${config.BASE_URL}/vigil/ai/query`, { question: q }, { headers });
      setAiResult(data);
      setHistory(prev => [q, ...prev.filter(h => h !== q)].slice(0, MAX_QUERY_HISTORY));
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      setAiError(/not configured/i.test(msg) ? 'no_ai' : msg);
    }
    setLoading(false);
  }, [question, headers]);

  const runQuery = useCallback(async (dsl) => {
    const q = (dsl || '').trim();
    if (!q || !targetPath.trim()) return;
    setRunLoading(true); setRunResult(null); setRunError('');
    try {
      const { data } = await axios.post(`${config.BASE_URL}/vigil/query/run`,
        { query: q, target: targetPath.trim() }, { headers });
      setRunResult(data);
    } catch (e) { setRunError(e.response?.data?.error || e.response?.data?.stderr || e.message); }
    setRunLoading(false);
  }, [targetPath, headers]);

  const copyQuery = () => {
    if (!aiResult?.query) return;
    navigator.clipboard.writeText(aiResult.query).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const currentDSL = mode === 'manual' ? manualQuery : aiResult?.query;
  const canRun = !!(currentDSL?.trim() && targetPath.trim());

  return (
    <div className="vrs-query">
      {/* mode tabs + target path */}
      <div className="vrs-card" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className={`vrs-btn${mode === 'ai' ? ' vrs-btn--primary' : ''}`} onClick={() => setMode('ai')}>✨ AI Translate</button>
          <button className={`vrs-btn${mode === 'manual' ? ' vrs-btn--primary' : ''}`} onClick={() => setMode('manual')}>⚡ Manual DSL</button>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label className="vrs-label" style={{ marginTop: 0, marginBottom: 0, whiteSpace: 'nowrap' }}>Target Path:</label>
          <input className="vrs-input" style={{ flex: 1, maxWidth: 500 }}
            placeholder="/path/to/repo or https://github.com/..."
            value={targetPath} onChange={e => setTargetPath(e.target.value)} />
        </div>
      </div>

      <div className="vrs-query-split">
        {/* left: input */}
        <div className="vrs-query-card">
          {mode === 'ai' ? (
            <>
              <textarea className="vrs-input vrs-textarea" rows={4}
                placeholder="Describe what you want to find in natural language…"
                value={question} onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) translate(); }} />
              <div className="vrs-q-examples-label">Examples</div>
              <div className="vrs-q-chips">
                {QUERY_EXAMPLES.map(ex => (
                  <button key={ex} className="vrs-q-chip" onClick={() => setQuestion(ex)}>{ex}</button>
                ))}
              </div>
              <div className="vrs-q-actions">
                <button className="vrs-q-translate-btn" onClick={translate} disabled={!question.trim() || loading}>
                  {loading ? <>Translating <span className="vrs-q-dots"><span /><span /><span /></span></> : '✨ Translate to DSL'}
                </button>
              </div>
            </>
          ) : (
            <>
              <textarea className="vrs-q-manual-editor" rows={6}
                placeholder="find flow from @user_input to @sql.execute"
                value={manualQuery} onChange={e => setManualQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) runQuery(manualQuery); }}
                spellCheck={false} />
              <div style={{ fontSize: '0.78rem', color: 'var(--bs-secondary-color)', marginBottom: '0.5rem' }}>
                Use @concepts for semantic matching · Ctrl+Enter to run
              </div>
            </>
          )}

          {history.length > 0 && (
            <>
              <div className="vrs-q-history-title">🕒 Recent</div>
              <ul className="vrs-q-history-list">
                {history.slice(0, 5).map((q, i) => (
                  <li key={i}>
                    <button className="vrs-q-history-item" onClick={() => { setQuestion(q); setMode('ai'); }}>
                      ↩ <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* right: results */}
        <div className="vrs-query-card">
          {mode === 'ai' && aiError === 'no_ai' && (
            <div className="vrs-error">
              <strong>AI not configured.</strong> Configure an AI provider in AI Settings.
            </div>
          )}
          {mode === 'ai' && aiError && aiError !== 'no_ai' && (
            <div className="vrs-error">{aiError}</div>
          )}

          {mode === 'ai' && loading && !aiResult && (
            <div className="vrs-empty"><div className="vrs-empty__icon">✨</div>
              Translating<span className="vrs-q-dots"><span /><span /><span /></span>
            </div>
          )}

          {mode === 'ai' && aiResult && (
            <>
              <div className="vrs-q-section-title">Generated DSL Query</div>
              <pre className="vrs-q-dsl-block">{highlightDSL(aiResult.query)}</pre>
              {aiResult.explanation && (
                <>
                  <div className="vrs-q-section-title">Explanation</div>
                  <div className="vrs-q-explain">{aiResult.explanation}</div>
                </>
              )}
              <div className="vrs-q-actions">
                <button className={`vrs-btn${copied ? ' vrs-btn--primary' : ''}`} onClick={copyQuery}>
                  {copied ? '✓ Copied' : '📋 Copy'}
                </button>
                <button className="vrs-q-run-btn" disabled={!canRun || runLoading} onClick={() => runQuery(aiResult.query)}>
                  {runLoading ? <>Running <span className="vrs-q-dots"><span /><span /><span /></span></> : '▶ Run Query'}
                </button>
                <button className="vrs-btn" onClick={() => openTab('write', { ruleContent: wrapDSLAsRule(aiResult.query, question) })}>
                  📝 {t('vigilStudio.promoteToRule','Promote to Rule')}
                </button>
              </div>
            </>
          )}

          {mode === 'manual' && (
            <>
              {manualQuery.trim() ? (
                <>
                  <div className="vrs-q-section-title">DSL Preview</div>
                  <pre className="vrs-q-dsl-block">{highlightDSL(manualQuery)}</pre>
                </>
              ) : (
                <div className="vrs-empty"><div className="vrs-empty__icon">⚡</div>Type a DSL query on the left</div>
              )}
              <div className="vrs-q-actions">
                <button className="vrs-q-run-btn" disabled={!canRun || runLoading} onClick={() => runQuery(manualQuery)}>
                  {runLoading ? <>Running <span className="vrs-q-dots"><span /><span /><span /></span></> : '▶ Run Query'}
                </button>
                {manualQuery.trim() && (
                  <button className="vrs-btn" onClick={() => openTab('write', { ruleContent: wrapDSLAsRule(manualQuery, 'Manual Query') })}>
                    📝 {t('vigilStudio.promoteToRule','Promote to Rule')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Query results */}
      {runError && (
        <div className="vrs-results-findings">
          <div className="vrs-error"><strong>Query Error:</strong> {runError}</div>
        </div>
      )}
      {runResult && !runLoading && (
        <div className="vrs-results-findings">
          <div className="vrs-results-header">
            <span className="vrs-results-badge">{runResult.total ?? runResult.findings?.length ?? 0} findings</span>
            {runResult.query && <code style={{ fontSize: '0.78rem', color: 'var(--bs-secondary-color)' }}>{runResult.query}</code>}
          </div>
          {(runResult.findings?.length > 0) ? (
            runResult.findings.map((f, i) => (
              <div key={i} className="vrs-finding-row" style={{ borderLeftColor: severityColor(f.severity) }}>
                <span className="vrs-finding-sev" style={{ color: severityColor(f.severity) }}>{(f.severity || 'info').toUpperCase()}</span>
                <span className="vrs-finding-rule">{f.rule_id || f.ruleId || '—'}</span>
                <span className="vrs-finding-file">{shortPath(f.file || f.location?.file)}</span>
                <span className="vrs-finding-line">L{f.line || f.location?.line || '?'}</span>
                <span className="vrs-finding-msg">{f.message || f.description || ''}</span>
              </div>
            ))
          ) : (
            <div className="vrs-no-findings">✓ No findings — query returned clean</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Main: VigilRuleStudio                                                     */
/* ═══════════════════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Joern CPG Tab — raw CPGQL (Scala) query playground                       */
/* ═══════════════════════════════════════════════════════════════════════════ */

const JOERN_HISTORY_KEY = 'vrs_joern_history';
const MAX_JOERN_HISTORY = 8;

function loadJoernHistory() {
  try { return JSON.parse(localStorage.getItem(JOERN_HISTORY_KEY) || '[]'); }
  catch { return []; }
}
function saveJoernHistory(h) {
  localStorage.setItem(JOERN_HISTORY_KEY, JSON.stringify(h.slice(0, MAX_JOERN_HISTORY)));
}

const JOERN_EXAMPLES = [
  {
    label: 'Find all eval() calls',
    lang: 'JavaScript',
    code: `cpg.call.name("eval").l`,
  },
  {
    label: 'eval() with first argument',
    lang: 'JavaScript',
    code: `cpg.call.name("eval").argument.order(1).code.l`,
  },
  {
    label: 'SQL calls with string concat (injection risk)',
    lang: 'Java',
    code: `cpg.call.name("executeQuery|execute|executeUpdate")
  .where(_.argument.code(".*\\+.*"))
  .l`,
  },
  {
    label: 'HTTP handler methods',
    lang: 'Go',
    code: `cpg.method.name(".*Handler.*").signature.l`,
  },
  {
    label: 'Find exec/system calls',
    lang: 'Any',
    code: `cpg.call.name("exec|system|popen|Exec|Run").l`,
  },
  {
    label: 'Taint flow: @RequestParam → executeQuery',
    lang: 'Java',
    code: `def source = cpg.parameter.name(".*[Ii]d|.*[Nn]ame|.*[Ii]nput")
def sink = cpg.call.name("executeQuery|execute|executeUpdate").argument
sink.reachableByFlows(source).l`,
  },
  {
    label: 'All method names',
    lang: 'Any',
    code: `cpg.method.name.l`,
  },
  {
    label: 'Methods in specific file',
    lang: 'Any',
    code: `cpg.method.filename(".*controllers.*").name.l`,
  },
  {
    label: 'Find password/secret identifiers',
    lang: 'Any',
    code: `cpg.identifier.name("(?i)password|secret|token|apikey").l`,
  },
  {
    label: 'Callers of a method',
    lang: 'Any',
    code: `cpg.method.name("authenticate").calledBy(cpg.method).name.l`,
  },
];

const JOERN_CPG_REF = [
  {
    category: 'Nodes',
    items: [
      { name: 'cpg.call', desc: 'All function/method calls' },
      { name: 'cpg.method', desc: 'All method definitions' },
      { name: 'cpg.identifier', desc: 'Variable identifiers' },
      { name: 'cpg.literal', desc: 'String/number literals' },
      { name: 'cpg.parameter', desc: 'Method parameters' },
      { name: 'cpg.controlStructure', desc: 'if/for/while/try blocks' },
      { name: 'cpg.typeDecl', desc: 'Class/struct/interface declarations' },
      { name: 'cpg.local', desc: 'Local variable declarations' },
      { name: 'cpg.member', desc: 'Class field members' },
      { name: 'cpg.file', desc: 'Source files in the CPG' },
    ],
  },
  {
    category: 'Traversal Filters',
    items: [
      { name: '.name("regex")', desc: 'Filter by name (supports regex)' },
      { name: '.code("regex")', desc: 'Filter by source code text' },
      { name: '.filename("regex")', desc: 'Filter by file path (regex)' },
      { name: '.lineNumber(n)', desc: 'Filter by line number' },
      { name: '.where(_.X)', desc: 'Filter by sub-traversal predicate' },
      { name: '.filterNot(_.X)', desc: 'Exclude matching nodes' },
    ],
  },
  {
    category: 'Navigation',
    items: [
      { name: '.argument', desc: 'Arguments of a call node' },
      { name: '.argument.order(n)', desc: 'Nth argument (1-based)' },
      { name: '.callee', desc: 'Method being called' },
      { name: '.caller', desc: 'Methods that call this node' },
      { name: '.calledBy(X)', desc: 'Methods that call X' },
      { name: '.astParent', desc: 'Parent in AST' },
      { name: '.inCall', desc: 'Calls containing this node' },
    ],
  },
  {
    category: 'Data Flow',
    items: [
      { name: '.reachableByFlows(src)', desc: 'Taint flow from src to this node (src must be .cast[CfgNode])' },
      { name: '.reachableBy(src)', desc: 'Whether src can reach this node' },
      { name: '.cast[CfgNode]', desc: 'Required cast for reachableByFlows sources in Joern 4.x' },
      { name: 'def source = cpg.X.cast[CfgNode]', desc: 'Multi-line flow: define source traversal' },
      { name: 'def sink = cpg.X.cast[CfgNode]', desc: 'Multi-line flow: define sink traversal' },
    ],
  },
  {
    category: 'Result Collection',
    items: [
      { name: '.l', desc: 'Collect to list (alias for .toList)' },
      { name: '.toList', desc: 'Collect all matching nodes to a list' },
      { name: '.size', desc: 'Count of matching nodes' },
      { name: '.code.l', desc: 'Get source code strings' },
      { name: '.name.l', desc: 'Get name strings' },
      { name: '.lineNumber.l', desc: 'Get line numbers' },
      { name: '.map(n => ...)', desc: 'Transform each node' },
      { name: '.foreach(println)', desc: 'Print each result' },
    ],
  },
];

function highlightCPGQL(code) {
  if (!code) return null;
  const keywords = new Set([
    'val', 'var', 'def', 'object', 'class', 'extends', 'with', 'import',
    'override', 'case', 'match', 'for', 'yield', 'if', 'else', 'while', 'do',
    'return', 'true', 'false', 'null', 'new', 'this', 'super',
  ]);
  const traversals = new Set([
    'cpg', 'call', 'method', 'identifier', 'literal', 'parameter',
    'controlStructure', 'typeDecl', 'local', 'member', 'file', 'argument',
    'callee', 'caller', 'inCall', 'astParent', 'ddgIn', 'ddgOut',
    'reachableByFlows', 'reachableBy', 'calledBy',
    'name', 'code', 'filename', 'lineNumber', 'where', 'filterNot',
    'order', 'size', 'map', 'foreach', 'filter', 'toList',
  ]);
  const tokens = [];
  let i = 0;
  while (i < code.length) {
    // String literals
    if (code[i] === '"') {
      let j = i + 1;
      while (j < code.length && code[j] !== '"') {
        if (code[j] === '\\') j++;
        j++;
      }
      tokens.push(<span key={i} style={{ color: '#22c55e' }}>{code.slice(i, j + 1)}</span>);
      i = j + 1;
      continue;
    }
    // Comments //
    if (code[i] === '/' && code[i + 1] === '/') {
      let j = code.indexOf('\n', i);
      if (j === -1) j = code.length;
      tokens.push(<span key={i} style={{ color: '#64748b', fontStyle: 'italic' }}>{code.slice(i, j)}</span>);
      i = j;
      continue;
    }
    // Dot traversals: .name, .call, etc.
    if (code[i] === '.') {
      let j = i + 1;
      while (j < code.length && /\w/.test(code[j])) j++;
      const word = code.slice(i + 1, j);
      if (traversals.has(word)) {
        tokens.push(<span key={i} style={{ color: '#a78bfa' }}>.{word}</span>);
        i = j;
        continue;
      }
    }
    // Words
    if (/[a-zA-Z_]/.test(code[i])) {
      let j = i;
      while (j < code.length && /\w/.test(code[j])) j++;
      const word = code.slice(i, j);
      if (keywords.has(word)) {
        tokens.push(<span key={i} style={{ color: '#f472b6' }}>{word}</span>);
      } else if (word === 'cpg') {
        tokens.push(<span key={i} style={{ color: '#38bdf8', fontWeight: 700 }}>{word}</span>);
      } else {
        tokens.push(word);
      }
      i = j;
      continue;
    }
    // Numbers
    if (/\d/.test(code[i])) {
      let j = i;
      while (j < code.length && /[\d.]/.test(code[j])) j++;
      tokens.push(<span key={i} style={{ color: '#fb923c' }}>{code.slice(i, j)}</span>);
      i = j;
      continue;
    }
    tokens.push(code[i]);
    i++;
  }
  return tokens;
}

function wrapCPGQLAsRule(cpgqlQuery, repoName) {
  const ruleId = `joern-${(repoName || 'adhoc').toLowerCase().replace(/[^a-z0-9]/g, '-')}-query`;
  return `rule ${ruleId}:
  version: "1.0.0"
  severity: info
  confidence: medium
  languages: [go, java, python, javascript, typescript, c, cpp]
  cwe: ["CWE-000"]  # TODO: set correct CWE

  # Fallback OpenGrep query (used if Joern not available)
  query: |
    find anything

  engines:
    joern:
      query: |
        ${cpgqlQuery.trim().replace(/\n/g, '\n        ')}

  report:
    message: "Joern CPG finding"
    evidence: [source_location]
`;
}

function JoernTab({ token, openTab }) {
  const { t } = useTranslation();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [cpgql, setCpgql]             = useState('cpg.call.name("eval").l');
  const [targetPath, setTargetPath]   = useState('');
  const [localRepos, setLocalRepos]   = useState([]);
  const [running, setRunning]         = useState(false);
  const [result, setResult]           = useState(null);
  const [error, setError]             = useState('');
  const [refOpen, setRefOpen]         = useState(false);
  const [exOpen, setExOpen]           = useState(true);
  const [history, setHistory]         = useState(loadJoernHistory);
  const [histOpen, setHistOpen]       = useState(false);

  // Load local repos for picker
  useEffect(() => {
    axios.get(`${config.BASE_URL}/repositories/local/details`, { headers })
      .then(r => setLocalRepos(r.data || []))
      .catch(() => {
        axios.get(`${config.BASE_URL}/repositories/local`, { headers })
          .then(r => setLocalRepos((r.data || []).map(n => ({ name: n }))))
          .catch(() => {});
      });
  }, [headers]);

  const runQuery = useCallback(async () => {
    const q = cpgql.trim();
    const t2 = targetPath.trim();
    if (!q || !t2) return;
    setRunning(true); setResult(null); setError('');
    try {
      const { data } = await axios.post(
        `${config.BASE_URL}/vigil/joern/query`,
        { cpgqlQuery: q, target: t2 },
        { headers }
      );
      setResult(data);
      setHistory(prev => {
        const entry = { query: q, target: t2, total: data.total || 0, ts: Date.now() };
        return [entry, ...prev.filter(h => h.query !== q)].slice(0, MAX_JOERN_HISTORY);
      });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
    setRunning(false);
  }, [cpgql, targetPath, headers]);

  useEffect(() => { saveJoernHistory(history); }, [history]);

  const promoteToRule = () => {
    const repoName = localRepos.find(r =>
      targetPath.includes(r.name)
    )?.name || 'query';
    openTab('write', { ruleContent: wrapCPGQLAsRule(cpgql, repoName) });
  };

  return (
    <div className="vrs-joern">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="vrs-card vrs-joern-header-card">
        <div className="vrs-joern-header-top">
          <div>
            <div className="vrs-joern-badge">Scala · CPGQL · Joern Engine</div>
            <h3 className="vrs-joern-title">Joern CPG Playground</h3>
            <p className="vrs-joern-desc">
              Write raw <strong>Joern CPGQL</strong> (Scala) queries against a local repository's Code Property Graph.
              Results use taint analysis, call graphs, and cross-file data flows — beyond what text-pattern rules can do.
            </p>
          </div>
          <div className="vrs-joern-header-links">
            <a href="https://docs.joern.io/cpgql/reference-language" target="_blank" rel="noopener noreferrer" className="vrs-joern-doc-link">📖 CPGQL Docs ↗</a>
            <a href="https://queries.joern.io" target="_blank" rel="noopener noreferrer" className="vrs-joern-doc-link">🔍 Query DB ↗</a>
          </div>
        </div>
        {/* Target selector */}
        <div className="vrs-joern-target-row">
          <label className="vrs-label" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>📁 Target repo:</label>
          {localRepos.length > 0 ? (
            <select
              className="vrs-joern-target-select"
              value={targetPath}
              onChange={e => setTargetPath(e.target.value)}
            >
              <option value="">— select repository —</option>
              {localRepos.map(r => {
                const basePath = `${config.BASE_URL.replace(/\/api\/v1$/, '').replace('http://localhost:8080', '')}/data/repositories/${r.name}`.replace(/^.*localhost:\d+/, '');
                const fullPath = targetPath && targetPath.includes(r.name) ? targetPath : '';
                return (
                  <option key={r.name} value={fullPath || `/Users/LM87YV/data/repositories/${r.name}`}>
                    {r.name}
                  </option>
                );
              })}
            </select>
          ) : null}
          <input
            className="vrs-joern-target-input"
            placeholder="/absolute/path/to/repository"
            value={targetPath}
            onChange={e => setTargetPath(e.target.value)}
          />
        </div>
      </div>

      {/* ── Main split ─────────────────────────────────────────────── */}
      <div className="vrs-joern-split">

        {/* LEFT: editor + examples ─────────────────────────────────── */}
        <div className="vrs-joern-left">

          {/* Editor */}
          <div className="vrs-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="vrs-joern-editor-header">
              <span className="vrs-joern-editor-label">⚡ CPGQL Query</span>
              <div style={{ display: 'flex', gap: '0.375rem' }}>
                <button className="vrs-btn vrs-btn--sm" onClick={() => setCpgql('')}>Clear</button>
                <button
                  className="vrs-btn vrs-btn--sm"
                  onClick={() => navigator.clipboard.writeText(cpgql)}
                >📋</button>
              </div>
            </div>
            <div style={{ position: 'relative' }}>
              <pre className="vrs-joern-highlight-layer" aria-hidden="true">
                <code>{highlightCPGQL(cpgql)}</code>
              </pre>
              <textarea
                className="vrs-joern-editor"
                value={cpgql}
                onChange={e => setCpgql(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) runQuery(); }}
                placeholder={'cpg.call.name("eval").l'}
                spellCheck={false}
                rows={8}
              />
            </div>
            <div className="vrs-joern-editor-footer">
              <span style={{ fontSize: '0.72rem', color: 'var(--bs-secondary-color)' }}>Ctrl+Enter to run</span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="vrs-btn" onClick={promoteToRule} disabled={!cpgql.trim()} title="Export as Vigil rule with engines.joern.query block">
                  📝 Add to Rule
                </button>
                <button
                  className="vrs-q-run-btn"
                  onClick={runQuery}
                  disabled={!cpgql.trim() || !targetPath.trim() || running}
                >
                  {running
                    ? <>Running <span className="vrs-q-dots"><span /><span /><span /></span></>
                    : '▶ Run'}
                </button>
              </div>
            </div>
          </div>

          {/* Examples */}
          <div className="vrs-card vrs-joern-collapsible">
            <button className="vrs-joern-collapsible-hdr" onClick={() => setExOpen(v => !v)}>
              <span>📚 Example Queries</span>
              <span>{exOpen ? '▲' : '▼'}</span>
            </button>
            {exOpen && (
              <div className="vrs-joern-examples">
                {JOERN_EXAMPLES.map(ex => (
                  <button
                    key={ex.label}
                    className="vrs-joern-example-item"
                    onClick={() => setCpgql(ex.code)}
                    title={ex.code}
                  >
                    <span className="vrs-joern-example-label">{ex.label}</span>
                    <span className="vrs-joern-example-lang">{ex.lang}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* History */}
          {history.length > 0 && (
            <div className="vrs-card vrs-joern-collapsible">
              <button className="vrs-joern-collapsible-hdr" onClick={() => setHistOpen(v => !v)}>
                <span>🕒 Recent Queries ({history.length})</span>
                <span>{histOpen ? '▲' : '▼'}</span>
              </button>
              {histOpen && (
                <ul className="vrs-joern-history-list">
                  {history.map((h, i) => (
                    <li key={i} className="vrs-joern-history-item">
                      <button onClick={() => { setCpgql(h.query); setTargetPath(h.target); }}>
                        <code className="vrs-joern-history-code">{h.query.length > 60 ? h.query.slice(0, 60) + '…' : h.query}</code>
                        <span className="vrs-joern-history-meta">
                          {h.total} {h.total === 1 ? 'match' : 'matches'} · {new Date(h.ts).toLocaleTimeString()}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: results + CPG reference ─────────────────────────── */}
        <div className="vrs-joern-right">

          {/* Results */}
          <div className="vrs-card vrs-joern-results-card">
            <div className="vrs-joern-results-hdr">
              <span className="vrs-joern-editor-label">Results</span>
              {result && (
                <span className={`vrs-joern-result-count${(result.total || 0) > 0 ? ' vrs-joern-result-count--hit' : ''}`}>
                  {result.total || 0} match{result.total === 1 ? '' : 'es'}
                </span>
              )}
            </div>

            {!result && !error && !running && (
              <div className="vrs-joern-empty">
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔗</div>
                <div>Write a CPGQL query and press <strong>Run</strong></div>
                <div style={{ fontSize: '0.75rem', marginTop: '0.375rem', opacity: 0.7 }}>
                  Requires Joern to be installed and on PATH
                </div>
              </div>
            )}

            {running && (
              <div className="vrs-joern-empty">
                <div className="vrs-joern-spinner" />
                <div style={{ marginTop: '0.75rem' }}>
                  Building CPG and running traversal…<br />
                  <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>This may take 15–60s on first run</span>
                </div>
              </div>
            )}

            {error && !running && (
              <div className="vrs-error" style={{ margin: '1rem' }}>
                <strong>Error:</strong> {error}
                {error.includes('not found') && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
                    Make sure <code>joern</code> is installed and the <strong>vigil binary</strong> can find it.
                  </div>
                )}
              </div>
            )}

            {result && !running && (
              <>
                {result.error && (
                  <div className="vrs-error" style={{ margin: '1rem 1rem 0' }}>
                    <strong>Engine warning:</strong> {result.error}
                  </div>
                )}
                {(result.findings || []).length === 0 && !result.error && (
                  <div className="vrs-joern-empty" style={{ padding: '2rem 1rem' }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>✅</div>
                    No matches found for this query in the target.
                  </div>
                )}
                {(result.findings || []).length > 0 && (
                  <div className="ce-table-wrap" style={{ margin: '0.75rem', borderRadius: 8 }}>
                    <table className="table table-sm table-hover">
                      <thead>
                        <tr>
                          <th>Severity</th>
                          <th>File</th>
                          <th>Line</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.findings.map((f, i) => {
                          let finding = f;
                          try { finding = typeof f === 'string' ? JSON.parse(f) : f; } catch {}
                          const sev = (finding.severity || 'info').toLowerCase();
                          const col = SEV[sev] || SEV.low;
                          return (
                            <tr key={i}>
                              <td>
                                <span className="ce-pill" style={{ background: col.bg, color: col.color, fontSize: '0.6rem' }}>
                                  {col.icon} {sev.toUpperCase()}
                                </span>
                              </td>
                              <td style={{ fontSize: '0.75rem', maxWidth: 200 }}>
                                <code style={{ wordBreak: 'break-all' }}>
                                  {(finding.location?.file || finding.file || '—').split('/').slice(-2).join('/')}
                                </code>
                              </td>
                              <td style={{ fontSize: '0.75rem' }}>
                                {finding.location?.line || finding.line || '—'}
                              </td>
                              <td style={{ fontSize: '0.8rem' }}>
                                {finding.message || '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {result.stderr && (
                  <details style={{ margin: '0.75rem', fontSize: '0.72rem' }}>
                    <summary style={{ cursor: 'pointer', color: 'var(--bs-secondary-color)' }}>Engine output</summary>
                    <pre style={{ background: 'var(--bs-tertiary-bg)', borderRadius: 6, padding: '0.5rem', marginTop: '0.5rem', fontSize: '0.7rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {result.stderr}
                    </pre>
                  </details>
                )}
              </>
            )}
          </div>

          {/* CPG Reference */}
          <div className="vrs-card vrs-joern-collapsible">
            <button className="vrs-joern-collapsible-hdr" onClick={() => setRefOpen(v => !v)}>
              <span>📖 CPG Traversal Reference</span>
              <span>{refOpen ? '▲' : '▼'}</span>
            </button>
            {refOpen && (
              <div className="vrs-joern-ref">
                {JOERN_CPG_REF.map(section => (
                  <div key={section.category} className="vrs-joern-ref-section">
                    <div className="vrs-joern-ref-category">{section.category}</div>
                    {section.items.map(item => (
                      <div key={item.name} className="vrs-joern-ref-item">
                        <code
                          className="vrs-joern-ref-name"
                          onClick={() => setCpgql(prev => prev + item.name)}
                          title="Click to append to query"
                        >
                          {item.name}
                        </code>
                        <span className="vrs-joern-ref-desc">{item.desc}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Main export: VigilRuleStudio                                              */
/* ═══════════════════════════════════════════════════════════════════════════ */

const TABS = [
  { id: 'browse',   icon: '🔍', label: 'vigilStudio.browse',   fallback: 'Browse'   },
  { id: 'write',    icon: '✏️',  label: 'vigilStudio.write',    fallback: 'Write'    },
  { id: 'generate', icon: '🤖', label: 'vigilStudio.generate',  fallback: 'Generate' },
  { id: 'query',    icon: '💬', label: 'vigilStudio.query',     fallback: 'Query'    },
  { id: 'joern',    icon: '🔗', label: 'vigilStudio.joern',     fallback: 'Joern CPG' },
];

export default function VigilRuleStudio() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return TABS.some(x => x.id === tab) ? tab : 'browse';
  });
  const [draftRule, setDraftRule] = useState(TEMPLATES.empty);
  const [similarRules, setSimilarRules] = useState([]);

  /* URL param sync */
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && TABS.some(x => x.id === tab) && tab !== activeTab) setActiveTab(tab);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Load rule from URL ?ruleId= */
  useEffect(() => {
    const ruleId = searchParams.get('ruleId');
    if (!ruleId || !token) return;
    axios.get(`${config.BASE_URL}/vigil/rules/${encodeURIComponent(ruleId)}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (data.rawContent) { setDraftRule(data.rawContent); setActiveTab('write'); }
      })
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTab = useCallback((tab, params = {}) => {
    if (params.ruleContent) setDraftRule(params.ruleContent);
    setActiveTab(tab);
    setSearchParams({ tab });
  }, [setSearchParams]);

  const handleTabClick = useCallback((tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  }, [setSearchParams]);

  const tabVariants = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    exit:    { opacity: 0, y: -8 },
  };

  return (
    <div className="vrs">
      {/* Header + tabs */}
      <div className="vrs-header">
        <div className="vrs-header__title-row">
          <span className="vrs-header__icon">🛡️</span>
          <div>
            <h2 className="vrs-header__title">{t('vigilStudio.title','Vigil Rule Studio')}</h2>
            <p className="vrs-header__sub">{t('vigilStudio.subtitle','Unified rule authoring, browsing, AI generation, and ad-hoc query in one place')}</p>
          </div>
        </div>
        <div className="vrs-tabs">
          {TABS.map(tab => (
            <button key={tab.id} className={`vrs-tab${activeTab === tab.id ? ' vrs-tab--active' : ''}`}
              onClick={() => handleTabClick(tab.id)}>
              <span className="vrs-tab__icon">{tab.icon}</span>
              {t(tab.label, tab.fallback)}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="vrs-body">
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} variants={tabVariants} initial="initial" animate="animate" exit="exit"
            transition={{ duration: 0.18 }}>
            {activeTab === 'browse' && (
              <BrowseTab token={token} openTab={openTab} similarRules={similarRules} />
            )}
            {activeTab === 'write' && (
              <WriteTab token={token} draftRule={draftRule} setDraftRule={setDraftRule} />
            )}
            {activeTab === 'generate' && (
              <GenerateTab token={token} openTab={openTab} onSimilarRulesChange={setSimilarRules} />
            )}
            {activeTab === 'query' && (
              <QueryTab token={token} openTab={openTab} />
            )}
            {activeTab === 'joern' && (
              <JoernTab token={token} openTab={openTab} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
